// ─── src/main.jsx ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, StrictMode, lazy, Suspense } from "react";

// ── Stale chunk guard ─────────────────────────────────────────────────────────
// After a new Cloudflare deployment the old cached index.html references
// chunk hashes that no longer exist on the CDN.  Two events need covering:
//
//   vite:preloadError , fires for <link rel="modulepreload"> failures (Vite injects this)
//   unhandledrejection, fires for dynamic import() failures (most chunk 404s land here)
//
// We reload once by navigating to the current URL (more cache-busting than
// location.reload()).  A timestamp in sessionStorage prevents infinite loops
// while still allowing a retry after 15 seconds if the first reload didn't help.
function _lwdChunkReload() {
  const now      = Date.now();
  const lastTime = parseInt(sessionStorage.getItem('lwd_chunk_reload') || '0', 10);
  if (now - lastTime > 15_000) {
    sessionStorage.setItem('lwd_chunk_reload', String(now));
    // Full navigation, forces the browser to re-fetch index.html and all assets
    window.location.href = window.location.href;
  }
}

// Matches every flavour of "a JS chunk 404'd and the CDN handed back the
// index.html shell", dynamic import failures AND the MIME-type rejection a
// stale <script type="module"> / modulepreload throws when its hashed chunk is
// gone after a deploy.
function _isStaleChunkMessage(msg) {
  if (!msg) return false;
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Failed to load module script') ||
    msg.includes('Expected a JavaScript') ||
    msg.includes('disallowed MIME type') ||
    (msg.includes('MIME type') && msg.includes('module'))
  );
}

window.addEventListener('vite:preloadError', _lwdChunkReload);

window.addEventListener('unhandledrejection', (event) => {
  // reason can be a TypeError with .message, or a raw string, or have .reason as a string
  const msg = event.reason?.message
    ?? (typeof event.reason === 'string' ? event.reason : '')
    ?? '';
  if (_isStaleChunkMessage(msg)) {
    event.preventDefault(); // suppress the console error
    _lwdChunkReload();
  }
});

// NOTE: we deliberately do NOT add a raw window 'error' listener that reloads on
// any <script>/<link> /assets/ load failure. Vite injects <link rel="modulepreload">
// tags whose preloads are routinely cancelled/failed by the browser on cold loads
// (harmless, the real dynamic import still succeeds). Reacting to those raw errors
// caused a reload loop that prevented admin from booting. vite:preloadError above is
// the correct signal: Vite fires it only when the preload AND the subsequent import
// both fail. Do not reintroduce a broad asset-error reload here.

// ── Sentry error capture, must init before any other imports render ──────────
import { initSentry } from "./lib/sentry.js";
import { DEPARTMENT_SLUGS, SECOND_LEVEL_SLUGS, SUBTYPE_SLUGS } from "./data/shopRoutes.js";
// Preview override: consume ?aura=private at startup so the flag persists early.
import "./chat/previewMode";
initSentry();

// ── Reader error boundary ─────────────────────────────────────────────────────
// Catches any render-time crash inside PublicationsReaderPage so the entire
// React tree stays alive. Without this, a crash (e.g. missing browser API)
// unmounts everything, leaves the dark body background, and the URL never
// syncs, exactly the "black screen" symptom on older mobile browsers.
class ReaderErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false, msg: null }; }
  static getDerivedStateFromError(e) { return { crashed: true, msg: e?.message || 'Unknown error' }; }
  componentDidCatch(e) { console.error('[ReaderErrorBoundary]', e); }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh',
          background: '#080706', gap: 16, padding: 24, textAlign: 'center',
        }}>
          <span style={{ fontSize: 32, opacity: 0.25, color: '#d39a21' }}>◈</span>
          <p style={{ fontFamily: 'Jost,sans-serif', fontSize: 14, color: 'rgba(255,255,255,0.5)', maxWidth: 320 }}>
            Something went wrong opening this issue.
          </p>
          <button
            onClick={() => { this.setState({ crashed: false, msg: null }); this.props.onBack?.(); }}
            style={{
              fontFamily: 'Jost,sans-serif', fontSize: 11, fontWeight: 600,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              color: '#d39a21', background: 'none',
              border: '1px solid #d39a21', padding: '9px 20px',
              borderRadius: 1, cursor: 'pointer', marginTop: 8,
            }}
          >Back to Publications</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { createRoot }           from "react-dom/client";
import { HelmetProvider, Helmet } from "react-helmet-async";

import { installLinkInterceptor } from "./utils/navigate";
import { applyThemeToDocument } from "./theme/ThemeLoader";
import { ThemeProvider, useTheme as useGlobalTheme } from "./theme/ThemeContext";
import { getDefaultMode } from "./theme/tokens";
import { ShortlistProvider } from "./shortlist/ShortlistContext";
import { AuraProvider }      from "./context/AuraProvider";
import { FloatingUiManager } from "./components/FloatingUiManager";
import { VendorAuthProvider } from "./context/VendorAuthContext";
import { CoupleAuthProvider } from "./context/CoupleAuthContext";
import { AdminAuthProvider, useAdminAuth } from "./context/AdminAuthContext";
import ProtectedCoupleRoute  from "./components/ProtectedCoupleRoute";
const AuraChat = lazy(() => import("./chat/AuraChat"));
import { useAura }           from "./stores/auraCore";
import CookieBanner          from "./components/CookieBanner";
import SiteFooter            from "./components/sections/SiteFooter.jsx";
import PreviewMiniFooter     from "./components/PreviewMiniFooter.jsx";
import GlobalAdminBar        from "./components/admin/GlobalAdminBar.jsx";
const GlobalCompare          = lazy(() => import("./compare/GlobalCompare.jsx"));
import GlobalSeoHead         from "./components/seo/GlobalSeoHead.jsx";
import SearchIdentity        from "./seo/SearchIdentity.jsx";
// The Search Manifest is the single source of truth for which static pages the client
// rendering layer must give engine identity to (Architecture/Search_Architecture.md).
// Path-keyed (not router-`page`-keyed) so it also covers pages whose internal route value
// differs from the URL — e.g. /editorial-standards renders through the Page Studio route.
import { needsRuntimeIdentity, LEGACY_DESTINATIONS } from "../supabase/functions/_shared/searchManifest.js";
import TrackingScripts       from "./components/TrackingScripts.jsx";
// import WhatsAppButton        from "./components/WhatsAppButton.jsx";
import SiteFavicon           from "./components/SiteFavicon.jsx";
const ComingSoonPage         = lazy(() => import("./pages/ComingSoonPage.jsx"));
import SiteModeGate          from "./components/SiteModeGate.jsx";

// ── Apply saved theme CSS variables BEFORE React renders ─────────────────────
applyThemeToDocument();

// ── Production console silencing, suppress Chrome extension + Supabase noise ─
// Keeps real app errors visible; drops extension injection, auth-schema, and
// favicon noise that pollutes the console in production.
if (import.meta.env.PROD) {
  const NOISE_PATTERNS = [
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /safari-extension:\/\//i,
    /extension context invalidated/i,
    /favicon\.ico/i,
    /net::ERR_/i,
    /ResizeObserver loop/i,
    /Non-Error promise rejection/i,
    // Supabase auth-schema advisory notices (not errors, just verbose)
    /pgrst\.jwt/i,
    /auth\.users/i,
    /Content-Security-Policy/i,
  ];
  const isNoise = (...args) =>
    args.some(a => typeof a === 'string' && NOISE_PATTERNS.some(p => p.test(a)));

  const _warn  = console.warn.bind(console);
  const _error = console.error.bind(console);
  console.warn  = (...a) => { if (!isNoise(...a)) _warn(...a);  };
  console.error = (...a) => { if (!isNoise(...a)) _error(...a); };
}

// ── Initialise return-after-outbound detection (runs once, passive) ──────────
import { initReturnDetection } from "./services/userEventService";
initReturnDetection();

// ── Visitor tracker (admin intelligence, fires silently) ─────────────────
import { initTracker, trackPageView } from "./lib/tracker";
import { supabase } from "./lib/supabaseClient";

// Legacy homepage variant (active_homepage === "home"). Lazy, the default is
// "h7", so this tree (SlimHero, sliders, grids, category.css) must not sit in the
// entry chunk that loads on every page.
const HomePage = lazy(() => import("./pages/HomePage.jsx"));
const VenueProfile           = lazy(() => import("./VenueProfile.jsx"));
// CountryTemplate removed, /category now renders ItalyPage with noIndex
const RegionPage             = lazy(() => import("./pages/RegionPage.jsx"));
const RegionCategoryPage     = lazy(() => import("./pages/RegionCategoryPage.jsx"));
const LWDStandard            = lazy(() => import("./pages/LWDStandard.jsx"));
const AboutLWD               = lazy(() => import("./pages/AboutLWD.jsx"));
const FaqPage                = lazy(() => import("./pages/FaqPage.jsx"));
const PressPage              = lazy(() => import("./pages/PressPage.jsx"));
const CareersPage            = lazy(() => import("./pages/CareersPage.jsx"));
const PricingApplicationPage = lazy(() => import("./pages/PricingApplication/PricingApplicationPage.jsx"));
const RegionGuidePage        = lazy(() => import("./pages/RegionGuidePage.jsx"));
const WeddingAtlasPage       = lazy(() => import("./pages/WeddingAtlasPage.jsx"));
const TaigenicPage           = lazy(() => import("./pages/TaigenicPage.jsx"));
const ContactLWD             = lazy(() => import("./pages/ContactLWD.jsx"));
const LWDPartnership         = lazy(() => import("./pages/LWDPartnership.jsx"));
const CmsPage                = lazy(() => import("./pages/CmsPage.jsx"));
const PageStudioRenderer     = lazy(() => import("./pages/PageStudioRenderer.jsx"));
const USAPage                = lazy(() => import("./pages/USAPage.jsx"));
const ItalyPage              = lazy(() => import("./pages/ItalyPage.jsx"));
const LocationPage           = lazy(() => import("./pages/LocationPage.jsx"));
const AllDestinationsPage    = lazy(() => import("./pages/AllDestinationsPage.jsx"));
const AdminDashboard         = lazy(() => import("./pages/AdminDashboard.jsx"));
const AdminLogin             = lazy(() => import("./pages/AdminLogin.jsx"));
const GoogleOAuthCallback    = lazy(() => import("./pages/GoogleOAuthCallback.jsx"));
const VendorDashboard        = lazy(() => import("./pages/VendorDashboard.jsx"));
const BusinessKnowledge      = lazy(() => import("./pages/KnowledgeStudio/BusinessKnowledge.jsx"));
const TeachAura              = lazy(() => import("./pages/KnowledgeStudio/TeachAura.jsx"));
const KnowledgeReview        = lazy(() => import("./pages/KnowledgeStudio/KnowledgeReview.jsx"));
const BuildPresence          = lazy(() => import("./pages/KnowledgeStudio/BuildPresence.jsx"));
const PartnershipHome        = lazy(() => import("./pages/Partnership/PartnershipHome.jsx"));
const IntelligenceDashboard  = lazy(() => import("./pages/Partnership/IntelligenceDashboard.jsx"));
const IntelligenceAssessment = lazy(() => import("./pages/Partnership/IntelligenceAssessment.jsx"));
const CompetitiveWatch       = lazy(() => import("./pages/Partnership/CompetitiveWatch.jsx"));
const ListingScreen          = lazy(() => import("./pages/Partnership/ListingScreen.jsx"));
const ReviewsScreen          = lazy(() => import("./pages/Partnership/ReviewsScreen.jsx"));
const LeadsScreen            = lazy(() => import("./pages/Partnership/LeadsScreen.jsx"));
const AnalyticsScreen        = lazy(() => import("./pages/Partnership/AnalyticsScreen.jsx"));
const ActivityScreen         = lazy(() => import("./pages/Partnership/ActivityScreen.jsx"));
const HistoryScreen          = lazy(() => import("./pages/Partnership/HistoryScreen.jsx"));
const ShowcaseScreen         = lazy(() => import("./pages/Partnership/ShowcaseScreen.jsx"));
const BookingsScreen         = lazy(() => import("./pages/Partnership/BookingsScreen.jsx"));
const MediaLibraryScreen     = lazy(() => import("./pages/Partnership/MediaLibraryScreen.jsx"));
const EditorialScreen        = lazy(() => import("./pages/Partnership/EditorialScreen.jsx"));
const AuraScreen             = lazy(() => import("./pages/Partnership/AuraScreen.jsx"));
const EventsScreen           = lazy(() => import("./pages/Partnership/EventsScreen.jsx"));
const MagazineScreen         = lazy(() => import("./pages/Partnership/MagazineScreen.jsx"));
const QuarterlyScreen        = lazy(() => import("./pages/Partnership/QuarterlyScreen.jsx"));
const MarketScreen           = lazy(() => import("./pages/Partnership/MarketScreen.jsx"));
const WorkQueueScreen        = lazy(() => import("./pages/Partnership/WorkQueueScreen.jsx"));
// Partnership Centre net-new screens (share one chunk).
const _PC = () => import("./pages/Partnership/PartnershipCentre.jsx");
const AchievementsScreen        = lazy(() => _PC().then((m) => ({ default: m.AchievementsScreen })));
const IntelligenceCentreScreen  = lazy(() => _PC().then((m) => ({ default: m.IntelligenceCentreScreen })));
const SuccessMomentsScreen      = lazy(() => _PC().then((m) => ({ default: m.SuccessMomentsScreen })));
const WinsScreen                = lazy(() => _PC().then((m) => ({ default: m.WinsScreen })));
const RecognitionScreen         = lazy(() => _PC().then((m) => ({ default: m.RecognitionScreen })));
const ComingNextScreen          = lazy(() => _PC().then((m) => ({ default: m.ComingNextScreen })));
const StrategicIntelligenceScreen = lazy(() => _PC().then((m) => ({ default: m.StrategicIntelligenceScreen })));
const YourStrategyScreen        = lazy(() => _PC().then((m) => ({ default: m.YourStrategyScreen })));
const PartnershipImpactScreen   = lazy(() => _PC().then((m) => ({ default: m.PartnershipImpactScreen })));
const RecommendedNextStepsScreen = lazy(() => _PC().then((m) => ({ default: m.RecommendedNextStepsScreen })));
const ForwardProjectionScreen   = lazy(() => _PC().then((m) => ({ default: m.ForwardProjectionScreen })));
const DigitalEstateScreen       = lazy(() => _PC().then((m) => ({ default: m.DigitalEstateScreen })));
const PartnershipReviewScreen   = lazy(() => _PC().then((m) => ({ default: m.PartnershipReviewScreen })));
const CoupleScreen           = lazy(() => import("./pages/Partnership/CoupleScreen.jsx"));
const CollectionsScreen      = lazy(() => import("./pages/Partnership/CollectionsScreen.jsx"));
const SectionPlaceholder     = lazy(() => import("./pages/Partnership/SectionPlaceholder.jsx"));
const ClientPortal           = lazy(() => import("./pages/ClientPortal.jsx"));
const VendorLogin            = lazy(() => import("./pages/VendorLogin.jsx"));
const VendorSignup           = lazy(() => import("./pages/VendorSignup.jsx"));
const VendorActivate         = lazy(() => import("./pages/VendorActivate.jsx"));
const VendorForgotPassword   = lazy(() => import("./pages/VendorForgotPassword.jsx"));
const VendorResetPassword    = lazy(() => import("./pages/VendorResetPassword.jsx"));
const VendorConfirmEmail     = lazy(() => import("./pages/VendorConfirmEmail.jsx"));
const CoupleSignup           = lazy(() => import("./pages/CoupleSignup.jsx"));
const CoupleLogin            = lazy(() => import("./pages/CoupleLogin.jsx"));
const CoupleForgotPassword   = lazy(() => import("./pages/CoupleForgotPassword.jsx"));
const CoupleResetPassword    = lazy(() => import("./pages/CoupleResetPassword.jsx"));
const CoupleConfirmEmail     = lazy(() => import("./pages/CoupleConfirmEmail.jsx"));
const WeddingPlannersPage    = lazy(() => import("./pages/WeddingPlannersPage.jsx"));
const PlannerProfilePage     = lazy(() => import("./pages/PlannerProfilePage.jsx"));
const ShortlistPage          = lazy(() => import("./pages/ShortlistPage.jsx"));
const RealWeddingsPage       = lazy(() => import("./pages/RealWeddingsPage.jsx"));
const RealWeddingDetailPage  = lazy(() => import("./pages/RealWeddingDetailPage.jsx"));
const GettingMarriedDashboard = lazy(() => import("./pages/GettingMarriedDashboard.jsx"));
const JoinPage = lazy(() => import("./pages/JoinPage.jsx"));
const FashionStudioPage   = lazy(() => import("./pages/FashionStudioPage.jsx"));
const MembershipEditionPage = lazy(() => import("./pages/MembershipEditionPage.jsx"));
const FashionStudioLandingPage  = lazy(() => import("./pages/FashionStudioLandingPage.jsx"));
const PartnerEnquiryPage = lazy(() => import("./pages/PartnerEnquiryPage.jsx"));
const ApplyForInclusionPage = lazy(() => import("./pages/ApplyForInclusion/ApplyForInclusionPage.jsx"));
const PricingPage = lazy(() => import("./pages/PricingPage.jsx"));
const ListingsPage = lazy(() => import("./pages/ListingsPage.jsx"));
const H2Page = lazy(() => import("./pages/H2Page.jsx"));
const AllWeddingVenuesPage = lazy(() => import("./pages/AllWeddingVenuesPage.jsx"));
const H3 = lazy(() => import("./pages/H3.jsx"));
const H4 = lazy(() => import("./pages/H4.jsx"));
const H6 = lazy(() => import("./pages/H6.jsx"));
const H7 = lazy(() => import("./pages/H7.jsx"));
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage.jsx"));
const AdvertisePage = lazy(() => import("./pages/AdvertisePage.jsx"));
const MostLovedPage = lazy(() => import("./pages/MostLovedPage.jsx"));
const ArtistryPage = lazy(() => import("./pages/Artistry/ArtistryPage.jsx"));
const PublicationsPage       = lazy(() => import("./pages/PublicationsPage.jsx"));
const PublicationsReaderPage    = lazy(() => import("./pages/PublicationsReaderPage.jsx"));
const MagazineCollaboratePage       = lazy(() => import("./pages/MagazineCollaboratePage.jsx"));
const VendorProofApprovalPage       = lazy(() => import("./pages/VendorProofApprovalPage.jsx"));
const VendorReachPage               = lazy(() => import("./pages/VendorReachPage.jsx"));
const MagazineVendorAnalyticsPage   = lazy(() => import("./pages/MagazineVendorAnalyticsPage.jsx"));
const PublicationsEmbedPage  = lazy(() => import("./pages/PublicationsEmbedPage.jsx"));
const PublicationStudio      = lazy(() => import("./pages/PublicationStudio.jsx"));
const SocialCanvas           = lazy(() => import("./pages/SocialCanvas.jsx"));
const ThumbnailRenderer      = lazy(() => import("./pages/PublicationStudio/ThumbnailRenderer.jsx"));
const MagazineHomePage     = lazy(() => import("./pages/Magazine/MagazineHomePage.jsx"));
const CategoryPage          = lazy(() => import("./pages/Magazine/CategoryPage.jsx"));
const MagazineArticlePage  = lazy(() => import("./pages/Magazine/MagazineArticlePage.jsx"));
const MagazinePreviewPage  = lazy(() => import("./pages/Magazine/MagazinePreviewPage.jsx"));
const FashionLandingPage   = lazy(() => import("./pages/Magazine/FashionLandingPage.jsx"));
const MagazineStudio         = lazy(() => import("./pages/MagazineStudio/index.jsx"));
const UniversalStudioRouter  = lazy(() => import("./pages/Studio/UniversalStudioRouter.jsx"));
const EditorialHomePage      = lazy(() => import("./pages/Editorial/EditorialHomePage.jsx"));
const EditorialMagazineHome  = lazy(() => import("./pages/Editorial/EditorialMagazineHome.jsx"));
const LWDEditHome            = lazy(() => import("./pages/Magazine/LWDEditHome.jsx"));
const EditorialArticlePage   = lazy(() => import("./pages/Editorial/EditorialArticlePage.jsx"));
const ShopPage             = lazy(() => import("./pages/Shop/ShopPage.jsx"));
const ShopProductPage      = lazy(() => import("./pages/Shop/ShopProductPage.jsx"));
const EditorialShowcase    = lazy(() => import("./pages/EditorialShowcase.jsx"));
const ShowcasePage         = lazy(() => import("./pages/ShowcasePage.jsx"));
const PreviewPage          = lazy(() => import("./pages/PreviewPage.jsx"));
const VendorPublicPage     = lazy(() => import("./pages/VendorPublicPage.jsx"));
const DdeShowcasePage          = lazy(() => import("./pages/DdeShowcasePage.jsx"));
const SixSensesShowcasePage    = lazy(() => import("./pages/SixSensesShowcasePage.jsx"));
const RitzLondonShowcasePage            = lazy(() => import("./pages/RitzLondonShowcasePage.jsx"));
const InterContinentalParkLanePage      = lazy(() => import("./pages/InterContinentalParkLanePage.jsx"));
const VenueProfilePage         = lazy(() => import("./pages/VenueProfilePage.jsx"));
const VenueReviewsPage         = lazy(() => import("./pages/VenueReviewsPage.jsx"));
const EventDetailPage          = lazy(() => import("./pages/EventDetailPage.jsx"));
const EventReviewPage          = lazy(() => import("./pages/EventReviewPage.jsx"));
const AuraDiscoveryDemoPage    = lazy(() => import("./pages/AuraDiscoveryDemoPage.jsx"));
const AuraPage                 = lazy(() => import("./pages/AuraPage.jsx"));
const DiscoverWithAuraPage     = lazy(() => import("./pages/DiscoverWithAuraPage.jsx"));
const AuraForVenuesPage        = lazy(() => import("./pages/AuraForVenuesPage.jsx"));
const VisionPage               = lazy(() => import("./pages/VisionPage.jsx"));
const AuraForVendorsPage       = lazy(() => import("./pages/AuraForVendorsPage.jsx"));
const LuxuryRegionPageTest     = lazy(() => import("./pages/LuxuryRegionPageTest.jsx"));
const GridResponsiveTest       = lazy(() => import("./pages/GridResponsiveTest.jsx"));
const NotFoundPage         = lazy(() => import("./pages/NotFoundPage.jsx"));
const UnsubscribePage      = lazy(() => import("./pages/UnsubscribePage.jsx"));
const CommunityPage             = lazy(() => import("./pages/CommunityPageStatic.jsx"));
const CommunityIntelligencePage = lazy(() => import("./pages/CommunityIntelligencePage.jsx"));
const CommunityFeedPage         = lazy(() => import("./pages/CommunityFeedPage.jsx"));
const CommunityJoinPage         = lazy(() => import("./pages/CommunityJoinPage.jsx"));
const CommunityMemberPage       = lazy(() => import("./pages/CommunityMemberPage.jsx"));
const AdvisoryPage         = lazy(() => import("./pages/AdvisoryPage.jsx"));
const OurApproachPage      = lazy(() => import("./pages/OurApproachPage.jsx"));
import { VENDORS }            from "./data/vendors.js";
import { prefetchDesignSettings } from "./hooks/useDesignSettings.js";
import {
  CATEGORY_SLUGS as REGISTRY_CATEGORY_SLUGS,
  ALIAS_MAP,
  getCanonicalCategorySlug,
} from "./data/categoryRegistry.js";

// Known Page Studio slugs, routes them to ps-page state so they don't fall
// through to the location catch-all. PageStudioRenderer (lazy) loads the actual
// content from localStorage or its own MOCK_PAGES fallback.
// Add new PS page slugs here when they are created in Page Studio.
const KNOWN_PS_SLUGS = new Set([
  "editorial-standards",
  "how-it-works",
  "destination-categories",
  "luxury-wedding-venues-in-lake-como",
  "tuscan-countryside-weddings",
  "italy-wedding-venues",
  "blog",
  "wedding-planning-philosophy",
  "summer-wedding-campaign-2026",
]);

// Warm design settings cache immediately, hero images appear without delay
prefetchDesignSettings();

// ── Lazy-loaded admin modules for bundle optimization ──────────────────────────
const ListingStudioPage = lazy(() => import("./pages/ListingStudio/ListingStudioPage.jsx"));

// ── Design system colors and fonts ───────────────────────────────────────────
const COLORS = {
  bg: "#0a0a0a",
  dark: "#1a1a1a",
  card: "#2a2a2a",
  white: "#ffffff",
  grey: "#a0a0a0",
  grey2: "#808080",
  border: "#404040",
  gold: "#d4af37",
  rose: "#f43f5e",
};
const FONTS = {
  normal: "'Nunito', sans-serif",
  display: "'Georgia', serif",
};

// ── Planner slug helpers ─────────────────────────────────────────────────────
function toSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
function getPlannerByIdOrSlug(idOrSlug) {
  if (!idOrSlug) return null;
  return VENDORS.find(
    (v) => v.category === "planner" && (v.id === idOrSlug || toSlug(v.name) === idOrSlug)
  ) || null;
}

// ── URL ↔ state helpers ──────────────────────────────────────────────────────
function stateToPath(pg, opts = {}) {
  if (typeof pg === "string" && pg.startsWith("portal-ph-")) return "/partnership/" + pg.slice(10);
  const { countrySlug, regionSlug, categorySlug, plannerSlug, weddingSlug, activationToken, locationType, locationSlug, boutiqueSlug } = opts;
  switch (pg) {
    // ── OPTION A: Dynamic location routes ──
    // Converts locationType + locationSlug back to URL path.
    // Examples: country:thailand → /thailand, region:italy:tuscany → /italy/tuscany
    case "location": {
      if (locationType === "country" && locationSlug) return `/${locationSlug}`;
      if (locationType === "region" && countrySlug && locationSlug) return `/${countrySlug}/${locationSlug}`;
      if (locationType === "city" && countrySlug && regionSlug && locationSlug) return `/${countrySlug}/${regionSlug}/${locationSlug}`;
      return "/";
    }
    case "region":           return `/${countrySlug}/${regionSlug}`;
    case "region-category": {
      // Canonical URL structure, matches the sitemap, the canonical tag, the
      // breadcrumb, and listing-profile URLs:
      //   Global  /[category]                  e.g. /wedding-venues
      //   Country /[country]/[category]         e.g. /austria/wedding-venues
      //   Region  /[country]/[region]/[category] e.g. /austria/tyrol/wedding-venues
      // The legacy /[country]/[category]-in/[region] form still resolves but 301s
      // here (see the parser's redirectToCanonical). One URL, one canonical.
      // Internal state may use countrySlug="world" and regionSlug="all", but these must NOT appear in public URLs
      if ((!countrySlug || countrySlug === "world") && (!regionSlug || regionSlug === "all")) return `/${categorySlug}`;
      if (countrySlug && countrySlug !== "world" && !regionSlug) return `/${countrySlug}/${categorySlug}`;
      if (countrySlug && countrySlug !== "world" && regionSlug && regionSlug !== "all") return `/${countrySlug}/${regionSlug}/${categorySlug}`;
      return "/";
    }
    case "planner-profile": {
      // Same region === country collapse as listing-profile below.
      if (countrySlug && regionSlug && regionSlug !== countrySlug) return `/${countrySlug}/${regionSlug}/wedding-planners/${plannerSlug}`;
      if (countrySlug)               return `/${countrySlug}/wedding-planners/${plannerSlug}`;
      return `/wedding-planners/${plannerSlug}`;
    }
    case "real-wedding-detail": return `/real-weddings/${weddingSlug}`;
    case "real-weddings":    return "/real-weddings";
    case "category":         return "/category";

    case "coming-soon":      return "/coming-soon";
    case "standard":         return "/the-lwd-standard";
    case "about":            return "/about";        // legacy alias (redirects to /about-us)
    case "about-us":         return "/about-us";
    case "vision":           return "/vision";
    case "press":            return "/press";
    case "careers":          return "/careers";
    case "region-guide":    return `/${opts.guideSlug || ''}`;
    case "wedding-atlas":   return "/wedding-guides";
    case "taigenic":         return "/taigenic";
    case "contact":          return "/contact";      // legacy alias (redirects to /contact-us)
    case "contact-us":       return "/contact-us";
    case "privacy":          return "/privacy";      // legacy alias (redirects to /privacy-policy)
    case "privacy-policy":   return "/privacy-policy";
    case "terms":            return "/terms";        // legacy alias (redirects to /terms-and-conditions)
    case "terms-and-conditions": return "/terms-and-conditions";
    case "ps-page":          return `/${opts?.psSlug || ""}`;
    case "cookie-policy":    return "/cookie-policy";
    case "reviews-policy":   return "/reviews-policy";
    case "support":          return "/support";
    case "unsubscribe":      return "/unsubscribe";
    case "partnership":      return "/partnership";
    case "admin":                return "/admin";
    case "admin-login":          return "/admin/login";
    case "admin-oauth-callback": return "/admin/oauth/callback";
    case "portal":           return "/portal";
    case "partnership-home":   return "/partnership";
    case "intelligence-dashboard": return "/partnership/dashboard";
    case "intelligence-assessment": return "/partnership/assessment";
    case "competitive-watch":  return "/partnership/competitive";
    case "portal-listing":     return "/partnership/listing";
    case "portal-reviews":     return "/partnership/reviews";
    case "portal-leads":       return "/partnership/leads";
    case "portal-analytics":   return "/partnership/analytics";
    case "portal-activity":    return "/partnership/activity";
    case "portal-history":     return "/partnership/history";
    case "portal-showcase":    return "/partnership/showcase";
    case "portal-bookings":    return "/partnership/bookings";
    case "portal-media":       return "/partnership/media";
    case "portal-editorial":   return "/partnership/editorial";
    case "portal-aura":        return "/partnership/aura";
    case "portal-events":      return "/partnership/events";
    case "portal-magazine":    return "/partnership/magazine";
    case "portal-quarterly":   return "/partnership/quarterly";
    case "portal-market":      return "/partnership/market";
    case "portal-work":        return "/partnership/work";
    case "portal-couple":      return "/partnership/couple";
    case "portal-collections": return "/partnership/collections";
    case "business-knowledge": return "/knowledge";
    case "teach-aura":         return "/knowledge/teach";
    case "knowledge-review":   return "/knowledge/review";
    case "build-presence":     return "/knowledge/build";
    case "vendor":           return "/vendor";
    case "vendor-login":           return "/vendor/login";
    case "vendor-signup":          return "/vendor/signup";
    case "vendor-activate":        return activationToken ? `/vendor/activate?token=${activationToken}` : "/vendor/activate";
    case "vendor-confirm-email":   return "/vendor/confirm-email";
    case "vendor-forgot-password": return "/vendor/forgot-password";
    case "vendor-reset-password":  return "/vendor/reset-password";
    case "couple-signup":          return "/getting-married/signup";
    case "couple-login":           return "/getting-married/login";
    case "couple-confirm-email":   return "/getting-married/confirm-email";
    case "couple-forgot-password": return "/getting-married/forgot-password";
    case "couple-reset-password":  return "/getting-married/reset-password";
    case "join":                  return "/join";
    case "membership-edition":    return `/edition/${opts.editionToken || ''}`;
    case "fashion-studio":        return "/fashion-studio";
    case "fashion-studio-app": {
      // Preserve the studio's deep-link params, the studio app is lazy-loaded, so
      // this URL-sync runs (and replaceState strips the URL) BEFORE the studio
      // mounts and reads them. Hardcoding ?studio=true here dropped boutique/gown.
      const cur = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const keep = new URLSearchParams({ studio: 'true' });
      for (const k of ['boutique', 'listingId', 'gown', 'collection', 'demo']) {
        const v = cur.get(k);
        if (v) keep.set(k, v);
      }
      return `/fashion-studio?${keep.toString()}`;
    }
    case "fashion-studio-showroom": return `/fashion-studio/${boutiqueSlug || ''}`;
    case "apply-for-inclusion":   return "/apply-for-inclusion";
    case "venue-pricing-application":   return "/venue-pricing-application";
    case "vendor-pricing-application":  return "/vendor-pricing-application";
    case "boutique-pricing-application": return "/boutique-pricing-application";
    case "advertise":             return "/advertise";
    case "pricing":               return "/pricing";
    case "listings":              return "/listings";
    case "h2":                    return "/h2";
    case "h3":                    return "/h3";
    case "h6":                    return "/h6";
    case "root":                  return "/";
    case "search":                return opts.searchQuery ? `/search?q=${encodeURIComponent(opts.searchQuery)}` : "/search";
    case "h4":                    return "/h4";
    case "h7":                    return "/h7";
    case "most-loved":            return "/most-loved";
    case "partner-enquiry":       return "/partner-enquiry";
    case "getting-married":  return "/getting-married";
    case "shortlist":        return "/shortlist";
    case "vendor-directory": return "/wedding-vendors";
    case "artistry-awards":  return "/artistry-awards";
    case "magazine":         return "/magazine";
    case "magazine-category": return `/magazine/category/${opts.magazineCategoryId || ''}`;
    case "magazine-fashion": return "/magazine/fashion";
    case "magazine-article": return `/magazine/${opts.magazineSlug || ''}`;
    case "magazine-studio":        return "/magazine-studio";
    case "publications":           return "/publications";
    case "publications-reader":    return `/publications/${opts.publicationsSlug || ''}`;
    case "publications-embed":     return `/publications/embed/${opts.publicationsEmbedSlug || ''}`;
    case "publication-studio":       return "/publication-studio";
    case "social-canvas":            return "/social-canvas";
    case "magazine-collaborate":        return `/magazine/collaborate/${opts.collaborateToken || ''}`;
    case "magazine-proof-approval":     return `/magazine/proof-approval/${opts.proofToken || ''}`;
    case "magazine-vendor-analytics":   return `/magazine/vendor-analytics/${opts.vendorAnalyticsToken || ''}`;
    case "magazine-reach":              return `/magazine/reach/${opts.reachIssueSlug || ''}/${opts.reachVendorSlug || ''}`;
    case "magazine-reader":          return `/magazine/read/${opts.magazineIssueSlug || ''}`;
    case "shop": {
      if (opts.shopBrand)    return `/shop/brand/${opts.shopBrand}`;
      if (opts.shopRetailer) return `/shop/retailer/${opts.shopRetailer}`;
      if (opts.shopDepartment && opts.shopCategory && opts.shopSubtype) return `/shop/${opts.shopDepartment}/${opts.shopCategory}/${opts.shopSubtype}`;
      if (opts.shopDepartment && opts.shopCategory) return `/shop/${opts.shopDepartment}/${opts.shopCategory}`;
      if (opts.shopDepartment) return `/shop/${opts.shopDepartment}`;
      return "/shop";
    }
    case "shop-product":             return `/shop/${opts.shopProductId || ''}`;
    case "editorial":                return "/lwd-edit";
    case "editorial-m2":             return "/lwd-edit/m2";
    case "editorial-article":        return `/lwd-edit/${opts.editorialSlug || ''}`;
    case "editorial-category":       return opts.editorialCategoryId ? `/lwd-edit/category/${opts.editorialCategoryId}` : "/lwd-edit";
    case "studio-edit": {
      const { entityType, slug, from } = opts;
      if (!entityType || !slug) return "/";
      const base = `/studio/edit/${entityType}/${slug}`;
      return from ? `${base}?from=${encodeURIComponent(from)}` : base;
    }
    case "vendor-public":    return `/vendor/${opts.vendorSlug || ''}`;
    case "venue-profile":    return `/venues/${opts.venueSlug || 'grand-tirolia'}`;
    case "venue-reviews":    return `/venues/${opts.venueSlug || ''}/reviews`;
    case "event-detail":     return `/events/${opts.eventSlug || ''}`;
    case "event-review":     return "/review";
    case "listing-profile": {
      const cs  = opts.countrySlug;
      const rs  = opts.regionSlug;
      const cat = opts.categorySlug || 'wedding-venues';
      const vs  = opts.venueSlug    || '';
      // Return null when params are incomplete, VenueProfile's replaceState
      // will set the correct canonical URL once it fetches the listing from DB.
      if (!cs || !rs || !vs) return null;
      // region === country is not a real region tier (Gibraltar the city-state; an
      // "italy/italy" data slip). The sitemap, the edge 301 and listingUrl.js all
      // collapse it; this did not, so loading /gibraltar/wedding-venues/{slug} rewrote
      // the address bar to /gibraltar/gibraltar/... and the canonical followed — a page
      // whose canonical 301s back to itself.
      if (rs === cs) return `/${cs}/${cat}/${vs}`;
      return `/${cs}/${rs}/${cat}/${vs}`;
    }
    case "dde-showcase":      return "/showcase/domaine-des-etangs";
    case "ritz-showcase":     return "/showcase/the-ritz-london";
    case "ic-park-lane-showcase": return "/showcase/intercontinental-london-park-lane";
    case "gt-showcase":       return "/showcase/grand-tirolia-kitzbuehel";
    case "sskrabey-showcase": return "/showcase/six-senses-krabey-island";
    case "showcase":         return `/showcase/${opts.showcaseSlug || ''}`;
    case "aura":             return "/aura";
    case "discover-with-aura": return "/discover-with-aura";
    case "aura-for-venues":  return "/aura-for-venues";
    case "aura-for-vendors": return "/aura-for-vendors";
    case "aura-discovery":   return "/discovery/aura";
    case "aura-v2-1":        return "/aura-v2-1";
    case "luxury-region-test": return "/test/luxury-region/amalfi-coast";
    case "advisory":          return "/advisory";
    case "advisory-audit":    return "/advisory?engagement=listing-audit";
    case "our-approach":      return "/our-approach";
    case "community":         return "/community";
    case "community-feed":    return "/community/feed";
    case "community-learning": return "/community/learning";
    case "community-learning-series": return `/community/learning/series/${opts.learningSeriesSlug || ''}`;
    case "community-learning-article": return opts.learningSeriesSlug
      ? `/community/learning/series/${opts.learningSeriesSlug}/${opts.learningArticleSlug || ''}`
      : `/community/learning/${opts.learningArticleSlug || ''}`;
    case "community-channel": return `/community/c/${opts.communityChannelSlug || ''}`;
    case "community-post":    return `/community/p/${opts.communityPostId || ''}`;
    case "community-join":    return `/community/join/${opts.inviteToken || ''}`;
    case "community-member":  return `/community/members/${opts.memberId || ''}`;
    case "all-destinations":  return "/all-destinations";
    case "preview":           return `/preview/${opts.previewToken || ''}`;
    default:                 return "/";
  }
}

function pathToState(pathname) {
  const clean = pathname.replace(/^\/+|\/+$/g, "");
  if (!clean) return { page: "root" };
  const statics = {
    category: "category", "the-lwd-standard": "standard",
    "coming-soon": "coming-soon",
    "about-us": "about-us", "contact-us": "contact-us", faq: "faq", press: "press", careers: "careers", partnership: "partnership",
    "privacy-policy": "privacy-policy", "terms-and-conditions": "terms-and-conditions", "cookie-policy": "cookie-policy", "reviews-policy": "reviews-policy", support: "support",
    admin: "admin", vendor: "vendor", couple: "couple", "real-weddings": "real-weddings", shortlist: "shortlist", "getting-married": "getting-married", join: "join", "artistry-awards": "artistry-awards", "partner-enquiry": "partner-enquiry", taigenic: "taigenic", "apply-for-inclusion": "apply-for-inclusion", "venue-pricing-application": "venue-pricing-application", "vendor-pricing-application": "vendor-pricing-application", "boutique-pricing-application": "boutique-pricing-application", pricing: "pricing", "most-loved": "most-loved", listings: "listings", "h2": "h2", "h3": "h3", "all-wedding-venues": "all-wedding-venues", "h4": "h4", "h6": "h6", advertise: "advertise", "all-destinations": "all-destinations",
    // Aura department pages
    "discover-with-aura": "discover-with-aura", "aura-for-venues": "aura-for-venues", "aura-for-vendors": "aura-for-vendors",
    // Nav-action aliases: "planning" links to LWD Standard page; "aura-discovery" mirrors /discovery/aura
    "planning": "standard", "aura-discovery": "aura-discovery", "vision": "vision",
    "wedding-guides": "wedding-atlas",
    "fashion-studio": (new URLSearchParams(window.location.search).get('studio') === 'true'
      || new URLSearchParams(window.location.search).get('boutique')
      || new URLSearchParams(window.location.search).get('listingId'))
      ? "fashion-studio-app"
      : "fashion-studio",
  };
  const parts = clean.split("/");
  // Unsubscribe landing page
  if (parts[0] === "unsubscribe" && parts.length === 1) return { page: "unsubscribe" };
  // Universal search results page, /search?q=...
  if (parts[0] === "search") return { page: "search", searchQuery: new URLSearchParams(window.location.search).get("q") || "" };
  // Client portal
  if (parts[0] === "portal") return { page: "portal" };
  // Redesigned Partnership Portal shell
  if (parts[0] === "partnership") {
    const map = { dashboard: "portal-intelligence", intelligence: "portal-intelligence", assessment: "intelligence-assessment", competitive: "competitive-watch", listing: "portal-listing", reviews: "portal-reviews", leads: "portal-leads", analytics: "portal-analytics", activity: "portal-activity", history: "portal-intelligence", showcase: "portal-showcase", bookings: "portal-bookings", media: "portal-media", editorial: "portal-editorial", aura: "portal-aura", events: "portal-events", magazine: "portal-magazine", quarterly: "portal-intelligence", market: "portal-market", work: "portal-work", couple: "portal-couple", collections: "portal-collections", moments: "portal-achievements", wins: "portal-achievements", achievements: "portal-achievements", recognition: "portal-recognition", next: "portal-next", strategic: "portal-strategic", strategy: "portal-strategy", impact: "portal-impact", recommend: "portal-recommend", projection: "portal-projection", estate: "portal-estate", review: "portal-review" };
    const placeholders = [];
    if (parts[1] && map[parts[1]]) return { page: map[parts[1]] };
    if (parts[1] && placeholders.includes(parts[1])) return { page: "portal-ph-" + parts[1] };
    return { page: "partnership-home" };
  }
  // Knowledge Studio
  if (parts[0] === "knowledge") {
    if (parts[1] === "teach") return { page: "teach-aura" };
    if (parts[1] === "review") return { page: "knowledge-review" };
    if (parts[1] === "build") return { page: "build-presence" };
    return { page: "business-knowledge" };
  }
  // /login, canonical couple login route (before it gets caught by location routes)
  if (parts[0] === "login" && parts.length === 1) return { page: "couple-login" };
  // Handle vendor auth subroutes first (before treating /vendor as static)
  if (parts[0] === "vendor" && parts[1] === "login" && parts.length === 2) return { page: "vendor-login" };
  if (parts[0] === "vendor" && parts[1] === "signup" && parts.length === 2) return { page: "vendor-signup" };
  if (parts[0] === "vendor" && parts[1] === "activate" && parts.length === 2) {
    const url = new URL("http://dummy" + pathname);
    const token = url.searchParams.get("token");
    return { page: "vendor-activate", activationToken: token };
  }
  if (parts[0] === "vendor" && parts[1] === "confirm-email" && parts.length === 2) return { page: "vendor-confirm-email" };
  if (parts[0] === "vendor" && parts[1] === "forgot-password" && parts.length === 2) return { page: "vendor-forgot-password" };
  if (parts[0] === "vendor" && parts[1] === "reset-password" && parts.length === 2) return { page: "vendor-reset-password" };
  if (parts[0] === "vendor" && parts[1] === "dashboard") return { page: "vendor" };
  // Public vendor profile: /vendor/{slug}, must come after all auth subroutes
  const VENDOR_AUTH_WORDS = ['login','signup','activate','confirm-email','forgot-password','reset-password','dashboard'];
  if (parts[0] === "vendor" && parts.length === 2 && !VENDOR_AUTH_WORDS.includes(parts[1])) {
    return { page: "vendor-public", vendorSlug: parts[1] };
  }
  // Handle admin auth subroutes
  if (parts[0] === "admin" && parts[1] === "login" && parts.length === 2) return { page: "admin-login" };
  if (parts[0] === "admin" && parts[1] === "oauth" && parts[2] === "callback") return { page: "admin-oauth-callback" };
  // Handle couple auth subroutes
  if (parts[0] === "couple" && parts[1] === "signup" && parts.length === 2) return { page: "couple-signup" };
  if (parts[0] === "couple" && parts[1] === "login" && parts.length === 2) return { page: "couple-login" };
  if (parts[0] === "getting-married" && parts[1] === "login" && parts.length === 2) return { page: "couple-login" };
  if (parts[0] === "getting-married" && parts[1] === "signup" && parts.length === 2) return { page: "couple-signup" };
  if (parts[0] === "getting-married" && parts[1] === "confirm-email" && parts.length === 2) return { page: "couple-confirm-email" };
  if (parts[0] === "getting-married" && parts[1] === "forgot-password" && parts.length === 2) return { page: "couple-forgot-password" };
  if (parts[0] === "getting-married" && parts[1] === "reset-password" && parts.length === 2) return { page: "couple-reset-password" };
  if (parts[0] === "couple" && parts[1] === "getting-married" && parts.length === 2) return { page: "getting-married" };
  // Handle real-weddings special routes
  if (parts[0] === "real-weddings" && parts.length === 1) return { page: "real-weddings" };
  if (parts[0] === "wedding-vendors" && parts.length === 1) return { page: "vendor-directory" };
  if (parts[0] === "real-weddings" && parts.length === 2) return { page: "real-wedding-detail", weddingSlug: parts[1] };
  // Handle Aura concierge (dedicated route)
  if (parts[0] === "aura" && parts.length === 1) return { page: "aura" };
  // Handle Discover with Aura (couples department)
  if (parts[0] === "discover-with-aura" && parts.length === 1) return { page: "discover-with-aura" };
  // Handle Aura for Venues
  if (parts[0] === "aura-for-venues" && parts.length === 1) return { page: "aura-for-venues" };
  // Handle Aura for Vendors
  if (parts[0] === "aura-for-vendors" && parts.length === 1) return { page: "aura-for-vendors" };
  // Handle Aura Discovery
  if (parts[0] === "discovery" && parts[1] === "aura" && parts.length === 2) return { page: "aura-discovery" };
  // Handle Aura v2.1 Test page
  if (parts[0] === "aura-v2-1" && parts.length === 1) return { page: "aura-v2-1" };
  // Handle Luxury Region Test page
  if (parts[0] === "test" && parts[1] === "luxury-region" && parts.length === 3) return { page: "luxury-region-test", testRegion: parts[2] };
  // Handle Grid Responsive Test page
  if (parts[0] === "test" && parts[1] === "grid" && parts.length === 2) return { page: "grid-responsive-test" };
  // Handle Amalfi Coast v2 unified SEO test
  if (parts[0] === "test" && parts[1] === "amalfi-coast-v2" && parts.length === 2) return { page: "amalfi-coast-v2" };
  // /italy/puglia used to route to PugliaPage, a Phase 3.1 demo that gated its whole
  // render on getRegionPageConfig(). That service is a stub returning null, so the page
  // could only ever render "Unable to load Puglia page configuration" — a region with 6
  // published listings served an empty shell to Google. It now falls through to the
  // generic region route like every other region.
  // Shop routes
  if (parts[0] === "shop") {
    if (parts.length === 1)
      return { page: "shop", shopDepartment: null, shopCategory: null, shopSubtype: null, shopBrand: null, shopRetailer: null };
    if (parts[1] === "brand" && parts[2])
      return { page: "shop", shopDepartment: null, shopCategory: null, shopSubtype: null, shopBrand: decodeURIComponent(parts[2]), shopRetailer: null };
    if (parts[1] === "retailer" && parts[2])
      return { page: "shop", shopDepartment: null, shopCategory: null, shopSubtype: null, shopBrand: null, shopRetailer: decodeURIComponent(parts[2]) };
    // Department / world landing: /shop/women, /shop/men, /shop/wedding
    if (parts.length === 2 && DEPARTMENT_SLUGS.includes(parts[1]))
      return { page: "shop", shopDepartment: parts[1], shopCategory: null, shopSubtype: null, shopBrand: null, shopRetailer: null };
    // Category / section: /shop/women/clothing, /shop/wedding/bridal-shoes
    if (parts.length === 3 && DEPARTMENT_SLUGS.includes(parts[1]) && SECOND_LEVEL_SLUGS.includes(parts[2]))
      return { page: "shop", shopDepartment: parts[1], shopCategory: parts[2], shopSubtype: null, shopBrand: null, shopRetailer: null };
    // Subtype: /shop/women/clothing/dresses
    if (parts.length === 4 && DEPARTMENT_SLUGS.includes(parts[1]) && SECOND_LEVEL_SLUGS.includes(parts[2]) && SUBTYPE_SLUGS.includes(parts[3]))
      return { page: "shop", shopDepartment: parts[1], shopCategory: parts[2], shopSubtype: parts[3], shopBrand: null, shopRetailer: null };
    // Fallback: flat product page (UUID or name slug). Flat category routes are retired.
    if (parts.length === 2)
      return { page: "shop-product", shopProductId: parts[1] };
  }
  // The LWD Edit routes (canonical, /lwd-edit; /the-edit kept as a legacy alias that
  // the app rewrites to /lwd-edit since stateToPath now emits /lwd-edit)
  const isEdit = parts[0] === "lwd-edit" || parts[0] === "the-edit";
  if (isEdit && parts.length === 1) return { page: "editorial" };
  // /lwd-edit/m2, magazine layout prototype. Must be checked BEFORE the
  // generic `parts[1] !== "category"` slug rule below, otherwise "m2" is
  // interpreted as an article slug.
  if (isEdit && parts[1] === "m2" && parts.length === 2) return { page: "editorial-m2" };
  if (isEdit && parts[1] === "category" && parts.length === 3) return { page: "editorial-category", editorialCategoryId: parts[2] };
  // /lwd-edit/category (no id), fall back to the index instead of leaking
  // through to LocationPage and treating "lwd-edit" as a country slug.
  if (isEdit && parts[1] === "category" && parts.length === 2) return { page: "editorial" };
  if (isEdit && parts.length === 2 && parts[1] !== "category") return { page: "editorial-article", editorialSlug: parts[1] };
  // /editorial/*, legacy redirect to /the-edit (same page state, stateToPath outputs /the-edit)
  if (parts[0] === "editorial" && parts.length === 1) return { page: "editorial" };
  if (parts[0] === "editorial" && parts[1] === "category" && parts.length === 3) return { page: "editorial-category", editorialCategoryId: parts[2] };
  if (parts[0] === "editorial" && parts[1] === "category" && parts.length === 2) return { page: "editorial" };
  if (parts[0] === "editorial" && parts.length === 2 && parts[1] !== "category") return { page: "editorial-article", editorialSlug: parts[1] };
  // Magazine routes
  if (parts[0] === "editorial-showcase" && parts.length === 1) return { page: "editorial-showcase" };
  // Venue showcase, static DDE page
  if (parts[0] === "showcase" && parts[1] === "domaine-des-etangs")       return { page: "dde-showcase" };
  if (parts[0] === "showcase" && parts[1] === "six-senses-krabey-island") return { page: "sskrabey-showcase" };
  // Venue showcase, static Ritz London editorial page
  if (parts[0] === "showcase" && parts[1] === "the-ritz-london")          return { page: "ritz-showcase" };
  if (parts[0] === "showcase" && parts[1] === "intercontinental-london-park-lane") return { page: "ic-park-lane-showcase" };
  // Venue showcase, static Grand Tirolia editorial page
  if (parts[0] === "showcase" && parts[1] === "grand-tirolia-kitzbuehel") return { page: "gt-showcase" };
  // Private review: /preview/{token}
  if (parts[0] === "preview" && parts.length === 2) return { page: "preview", previewToken: parts[1] };
  // Venue showcase: /showcase/{slug}
  if (parts[0] === "showcase" && parts.length === 2) return { page: "showcase", showcaseSlug: parts[1] };
  // ── DEPRECATED OLD URLs (for redirect only) ──
  // Old format: /destinations/* → canonical geo URLs. LEGACY_DESTINATIONS is the single
  // authoritative map (searchManifest.js) — also consumed by _generate_redirects.mjs to
  // write public/_redirects' edge-level copy of the same rules. See Project 3 (404
  // investigation): this used to be two independently hand-maintained maps that drifted
  // and, in several cases, redirected users into a region/city page that was never
  // promoted to REGIONAL_HUBS — a redirect into a 404.
  if (parts[0] === "destinations") {
    const target = (parts[1] && LEGACY_DESTINATIONS[parts[1]]) || (parts.length === 1 ? "/all-destinations" : "/");
    if (typeof window !== "undefined") { window.location.replace(target); }
    return { page: "root" };
  }
  // Old format: /venues → redirect to /wedding-venues
  if (parts[0] === "venues" && parts.length === 1) return { page: "region-category", countrySlug: null, regionSlug: null, categorySlug: "wedding-venues", redirectToCanonical: true };
  // Old format: /venues/{slug} → redirect to canonical
  if (parts[0] === "venues" && parts.length === 2) return { page: "listing-profile", venueSlug: parts[1], redirectToCanonical: true };
  // Venue reviews page: /venues/{slug}/reviews → also deprecated
  if (parts[0] === "venues" && parts.length === 3 && parts[2] === "reviews") return { page: "venue-reviews", venueSlug: parts[1] };
  // Event detail: /events/{slug}
  if (parts[0] === "events" && parts.length === 2) return { page: "event-detail", eventSlug: parts[1] };
  // Event attendee review: /review?token=UUID
  if (parts[0] === "review" && parts.length === 1) return { page: "event-review" };
  // Old format: /wedding-venues/{slug} → redirect to canonical
  if (parts[0] === "wedding-venues" && parts.length === 2) return { page: "listing-profile", venueSlug: parts[1], redirectToCanonical: true };
  // Universal studio editor: /studio/edit/{entityType}/{slug}
  if (parts[0] === "studio" && parts[1] === "edit" && parts.length === 4) {
    return { page: "studio-edit", entityType: parts[2], slug: decodeURIComponent(parts[3]) };
  }
  if (parts[0] === "magazine-studio" && parts.length === 1) return { page: "magazine-studio" };
  if (parts[0] === "publications" && parts.length === 1) return { page: "publications" };
  if (parts[0] === "publications" && parts[1] === "embed" && parts.length === 3) return { page: "publications-embed", publicationsEmbedSlug: parts[2] };
  if (parts[0] === "publications" && parts.length === 2) return { page: "publications-reader", publicationsSlug: parts[1] };
  if (parts[0] === "publication-studio") return { page: "publication-studio" };
  if (parts[0] === "social-canvas")      return { page: "social-canvas" };
  if (parts[0] === "magazine" && parts[1] === "collaborate" && parts.length === 3) return { page: "magazine-collaborate", collaborateToken: parts[2] };
  if (parts[0] === "magazine" && parts[1] === "proof-approval" && parts[2]) return { page: "magazine-proof-approval", proofToken: parts[2] };
  if (parts[0] === "magazine" && parts[1] === "vendor-analytics" && parts[2]) return { page: "magazine-vendor-analytics", vendorAnalyticsToken: parts[2] };
  if (parts[0] === "magazine" && parts[1] === "reach" && parts.length === 4) return { page: "magazine-reach", reachIssueSlug: parts[2], reachVendorSlug: parts[3] };
  if (parts[0] === "magazine" && parts[1] === "read" && parts.length === 3) return { page: "magazine-reader", magazineIssueSlug: parts[2] };
  // Dev-only renderer for thumbnail generation, see scripts/generate-template-thumbnails.mjs
  if (parts[0] === "studio-thumbnail" && parts.length === 2) return { page: "studio-thumbnail", templateId: parts[1] };
  // /magazine public routes → redirect to /the-edit (stateToPath outputs the new
  // URL; redirectToCanonical triggers window.location.replace to rewrite it).
  if (parts[0] === "magazine" && parts.length === 1) return { page: "editorial", redirectToCanonical: true };
  if (parts[0] === "magazine" && parts[1] === "category" && parts.length === 3) return { page: "magazine-category", magazineCategoryId: parts[2] };
  if (parts[0] === "magazine" && parts[1] === "fashion" && parts.length === 2) return { page: "editorial", redirectToCanonical: true };
  if (parts[0] === "magazine" && parts[1] === "preview" && parts.length === 2) return { page: "magazine-preview" };
  if (parts[0] === "magazine" && parts.length === 2 && parts[1] !== "category" && parts[1] !== "fashion" && parts[1] !== "preview") return { page: "editorial-article", editorialSlug: parts[1] };
  // Community routes
  if (parts[0] === "advisory"    && parts.length === 1) return { page: "advisory" };
  if (parts[0] === "our-approach" && parts.length === 1) return { page: "our-approach" };
  if (parts[0] === "community" && parts.length === 1) return { page: "community" };
  if (parts[0] === "community" && parts[1] === "feed" && parts.length === 2) return { page: "community-feed" };
  if (parts[0] === "community" && parts[1] === "learning" && parts.length === 2) return { page: "community-learning" };
  if (parts[0] === "community" && parts[1] === "learning" && parts[2] === "series" && parts.length === 4) return { page: "community-learning-series", learningSeriesSlug: parts[3] };
  if (parts[0] === "community" && parts[1] === "learning" && parts[2] === "series" && parts.length === 5) return { page: "community-learning-article", learningSeriesSlug: parts[3], learningArticleSlug: parts[4] };
  if (parts[0] === "community" && parts[1] === "learning" && parts.length === 3) return { page: "community-learning-article", learningSeriesSlug: null, learningArticleSlug: parts[2] };
  if (parts[0] === "community" && parts[1] === "c" && parts.length === 3) return { page: "community-channel", communityChannelSlug: parts[2] };
  if (parts[0] === "community" && parts[1] === "p" && parts.length === 3) return { page: "community-post", communityPostId: parts[2] };
  if (parts[0] === "community" && parts[1] === "join" && parts.length === 3) return { page: "community-join", inviteToken: parts[2] };
  if (parts[0] === "community" && parts[1] === "members" && parts.length === 3) return { page: "community-member", memberId: parts[2] };
  // Dynamic region guide routes: any /{slug}-wedding-guide slug
  if (parts.length === 1 && parts[0].endsWith('-wedding-guide')) {
    return { page: "region-guide", guideSlug: parts[0] };
  }

  // Static routes: non-location single-segment paths (admin, about, contact, etc.)
  if (parts.length === 1 && statics[parts[0]]) return { page: statics[parts[0]] };

  // /fashion-studio/{slug}, direct boutique showroom (shareable + snapshot target).
  // Must precede the generic /:category/:slug listing fallback below.
  if (parts[0] === "fashion-studio" && parts.length === 2) {
    return { page: "fashion-studio-showroom", boutiqueSlug: parts[1] };
  }

  // /edition/{token}, the private per-recipient Membership Edition. Never listed,
  // never in the sitemap, and the pages live in a private bucket, so the token is
  // the only way in.
  if (parts[0] === "edition" && parts.length === 2) {
    return { page: "membership-edition", editionToken: parts[1] };
  }

  // ── Legacy slug aliases → canonical (301 redirect via needsRedirect) ──────────
  const SLUG_ALIASES = { "about": "about-us", "contact": "contact-us", "privacy": "privacy-policy", "terms": "terms-and-conditions", "cookies": "cookie-policy", "list-your-business": "apply-for-inclusion" };
  if (parts.length === 1 && SLUG_ALIASES[parts[0]]) {
    return { page: SLUG_ALIASES[parts[0]], redirectToCanonical: true };
  }

  // ── NEW URL STRUCTURE: Category Pages (Phase 1) ────────────────────────────────
  // Uses existing RegionCategoryPage component with different data context
  // /[category] → global, /[country]/[category] → country, /[country]/[category]-in/[region] → region
  const CATEGORY_SLUGS = REGISTRY_CATEGORY_SLUGS;

  // ── Alias redirects, e.g. /wedding-vendors/bridal-boutiques → /bridal-boutiques ──
  {
    const aliasKey1 = parts.length === 1 ? `/${parts[0]}` : null;
    const aliasKey2 = parts.length === 2 ? `/${parts[0]}/${parts[1]}` : null;
    const aliasKey  = aliasKey1 || aliasKey2;
    if (aliasKey && ALIAS_MAP[aliasKey]) {
      const canonicalSlug = ALIAS_MAP[aliasKey].replace(/^\//, '');
      return { page: 'region-category', countrySlug: null, regionSlug: null, categorySlug: canonicalSlug, redirectToCanonical: true };
    }
  }

  // /[category], Global category (e.g. /wedding-venues)
  if (parts.length === 1 && CATEGORY_SLUGS.includes(parts[0])) {
    return { page: "region-category", countrySlug: null, regionSlug: null, categorySlug: parts[0] };
  }

  // /[country]/[category], Country category (e.g. /england/wedding-venues)
  if (parts.length === 2 && CATEGORY_SLUGS.includes(parts[1])) {
    return { page: "region-category", countrySlug: parts[0], regionSlug: null, categorySlug: parts[1] };
  }

  // /[country]/[category]-in/[region], LEGACY region-category format.
  // Resolves to the same page but redirects to the canonical /[country]/[region]/[category]
  // (e.g. /england/wedding-venues-in/surrey → /england/surrey/wedding-venues).
  if (parts.length === 3 && parts[1].includes('-in')) {
    const [categoryPart, _] = parts[1].split('-in');
    if (CATEGORY_SLUGS.includes(categoryPart)) {
      return { page: "region-category", countrySlug: parts[0], regionSlug: parts[2], categorySlug: categoryPart, redirectToCanonical: true };
    }
  }

  // ── Page Studio fallback: serve any published PS page by slug ────────────────
  // Must sit BEFORE the location catch-all so /how-it-works doesn't get treated as a country.
  // Checks localStorage first (admin-edited versions). For slugs in KNOWN_PS_SLUGS,
  // routes to ps-page even before admin has seeded localStorage, PageStudioRenderer
  // (lazy-loaded) has its own MOCK_PAGES fallback to supply the content.
  if (parts.length === 1) {
    try {
      const psPages = JSON.parse(localStorage.getItem("ps_pages") || "[]");
      const psMatch = psPages.find(p => p.slug === parts[0] && p.status === "published");
      if (psMatch) return { page: "ps-page", psSlug: parts[0] };
    } catch {}
    // Route known PS slugs even when localStorage is empty (first visit)
    if (KNOWN_PS_SLUGS.has(parts[0])) return { page: "ps-page", psSlug: parts[0] };
  }

  // ── ALL location routes resolve through LocationPage ──
  // No country-specific privileged routing. Supabase locations table is the single authority.
  // /italy, /thailand, /france, all treated identically.
  // LocationPage queries Supabase first, falls back to static geo.js only for migration.
  if (parts.length === 1) return { page: "location", locationType: "country", locationSlug: parts[0] };

  // /wedding-planners/{slug}, Global planner profile (no country/region context)
  if (parts.length === 2 && parts[0] === "wedding-planners") {
    return { page: "planner-profile", countrySlug: null, regionSlug: null, categorySlug: "wedding-planners", plannerSlug: parts[1] };
  }

  // Guard: /magazine/category without an ID → fall back to magazine home
  if (parts.length === 2 && parts[0] === "magazine") return { page: "magazine" };

  // /:category/:slug, listing profile without country context (fallback when country_slug missing)
  if (parts.length === 2 && CATEGORY_SLUGS.includes(parts[0]) && !CATEGORY_SLUGS.includes(parts[1])) {
    return { page: "listing-profile", categorySlug: parts[0], venueSlug: parts[1] };
  }
  // ── Navigation Contract safety net, recover legacy / non-canonical listing
  // links and 301 to the canonical URL. Protects old bookmarks, Google, emails
  // and social posts, and catches anything a URL builder might still miss.
  // Runs AFTER every named 2-part route (magazine/community/events/publications/
  // canonical category) so it only sees genuinely orphaned listing paths.
  if (parts.length === 2) {
    // Legacy venue/vendor/planner prefixes, or any singular listingType alias
    // that normalises to a real category (e.g. /photographer/x, /florist/x). The
    // slug is resolved against the listings table (which holds venues, planners
    // AND vendors), yielding the true category, then we 301 to the canonical URL.
    const canon = getCanonicalCategorySlug(parts[0]);
    const isLegacyPrefix = ["venue", "venues", "vendor", "vendors", "planner", "planners"].includes(parts[0]);
    const isCategoryAlias = canon !== parts[0] && CATEGORY_SLUGS.includes(canon);
    if (isLegacyPrefix || isCategoryAlias) {
      return { page: "listing-profile", venueSlug: parts[1], redirectToCanonical: true };
    }
  }
  if (parts.length === 2) return { page: "region", countrySlug: parts[0], regionSlug: parts[1] };
  if (parts.length === 3) {
    // Modern format: /country/region/category → region-category grid
    // Old format: /country/category/slug → DEPRECATED, redirect to canonical
    if (CATEGORY_SLUGS.includes(parts[2])) {
      // New format: /country/region/category (e.g. /england/somerset/wedding-venues)
      return { page: "region-category", countrySlug: parts[0], regionSlug: parts[1], categorySlug: parts[2] };
    }
    // /[country]/wedding-planners/[slug], Country-scoped planner profile
    if (parts[1] === "wedding-planners") {
      return { page: "planner-profile", countrySlug: parts[0], regionSlug: null, categorySlug: "wedding-planners", plannerSlug: parts[2] };
    }
    if (CATEGORY_SLUGS.includes(parts[1])) {
      // Old format: /country/category/slug (e.g. /italy/wedding-venues/villa-balbiano)
      return { page: "listing-profile", countrySlug: parts[0], regionSlug: null, categorySlug: parts[1], venueSlug: parts[2], redirectToCanonical: true };
    }
    // Fallback: treat as new format (region-category)
    return { page: "region-category", countrySlug: parts[0], regionSlug: parts[1], categorySlug: parts[2] };
  }
  if (parts.length === 4) {
    const [countrySlug, regionSlug, part2, part3] = parts;
    // /country/region/city/category  (city pages, part2 not a category, part3 is)
    if (!CATEGORY_SLUGS.includes(part2) && CATEGORY_SLUGS.includes(part3)) {
      return { page: "city-category", countrySlug, regionSlug, citySlug: part2, categorySlug: part3 };
    }
    // /country/region/category/slug  (listing profile, default)
    return { page: "listing-profile", countrySlug, regionSlug, categorySlug: part2, venueSlug: part3 };
  }
  // /{country}/{region}/{category}/{slug}/reviews
  if (parts.length === 5 && parts[4] === "reviews") {
    return { page: "venue-reviews", countrySlug: parts[0], regionSlug: parts[1], categorySlug: parts[2], venueSlug: parts[3] };
  }
  return { page: "not-found" };
}

// ── Aura suppressor for admin/vendor pages ───────────────────────────────────
// True when the visitor is inside a private-review preview session AND on a
// sealed content page (listing, showcase, reviews). Used to keep these pages
// inside the curated, pricing-free experience: minimal footer, no Aura, no nav
// links back out to the public site.
const SEALED_PREVIEW_PAGES = new Set(['listing-profile', 'venue-reviews', 'showcase', 'editorial-article']);
function isSealedPreview(page) {
  if (!SEALED_PREVIEW_PAGES.has(page)) return false;
  try { if (sessionStorage.getItem('preview_session')) return true; } catch { /* partitioned storage */ }
  // The LWD Edit "proof" link inside a private review opens the article in a new
  // tab (no shared sessionStorage), so a ?preview=1 marker keeps it in the sealed
  // reading view: no top nav or footer back out to the public site.
  if (page === 'editorial-article') {
    try { return new URLSearchParams(window.location.search).get('preview') === '1'; } catch { return false; }
  }
  return false;
}

// Forces chatUiState back to "closed" when navigating into any dashboard page.
// Prevents Aura persisting open from a previous public page visit, and prevents
// the lwd_aura_engage bridge from rendering the chat inside an admin window.
function AuraSuppressor({ active }) {
  const closeChat = useAura((s) => s.closeChat);
  useEffect(() => {
    if (active) closeChat();
  }, [active, closeChat]);
  return null;
}

// ── HiddenRouteRedirect ─────────────────────────────────────────────────────
// Mounted in place of any page that has been temporarily hidden from the
// public site (e.g. /real-weddings while it's being rebuilt). On mount it
// fires the supplied redirect handler, usually goHome, so a direct URL
// hit lands the visitor somewhere live instead of rendering the legacy
// page or showing a blank screen. Render path returns null so there is
// no flash of content before the redirect resolves.
function HiddenRouteRedirect({ onRedirect }) {
  useEffect(() => {
    if (typeof onRedirect === "function") onRedirect();
  }, [onRedirect]);
  return null;
}

// ── Admin Route Wrapper (properly calls hooks at top level) ──────────────────
function AdminRoute({ onBack, onNavigate }) {
  const { isAuthenticated, isAdmin, loading } = useAdminAuth();

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#0B0906", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading-primary,'Gilda Display',serif)", fontSize: 28, letterSpacing: "0.15em", color: "#d39a21" }}>Loading LWD</div>;
  }

  // Require both an authenticated session AND admin role.
  // An authenticated vendor user must not see the admin dashboard.
  if (!isAuthenticated || !isAdmin) {
    return <AdminLogin onBack={onBack} />;
  }

  return (
    <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0B0906", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading-primary,'Gilda Display',serif)", fontSize: 28, letterSpacing: "0.15em", color: "#d39a21" }}>Loading LWD</div>}>
      <AdminDashboard onBack={onBack} onNavigate={onNavigate} />
    </Suspense>
  );
}

// ── App (router + providers in one place) ────────────────────────────────────
function App() {
  // Parse initial URL so direct links & refreshes work
  const initial = pathToState(window.location.pathname);

  const themeCtx = useGlobalTheme();
  const darkMode = themeCtx.darkMode ?? (getDefaultMode() === 'dark');
  const toggleDark = themeCtx.toggleDark ?? (() => {});

  const [page, setPage] = useState(initial.page);
  // Active homepage variant, loaded from platform_settings on mount
  const [activeHomepage, setActiveHomepage] = useState("h7");
  const [categoryRegion, setCategoryRegion] = useState(null);
  const [activeCountrySlug, setActiveCountrySlug] = useState(initial.countrySlug || null);
  const [activeRegionSlug, setActiveRegionSlug] = useState(initial.regionSlug || null);
  const [activeCategorySlug, setActiveCategorySlug] = useState(initial.categorySlug || null);
  const [activePlannerSlug, setActivePlannerSlug] = useState(initial.plannerSlug || null);
  const [activeBoutiqueSlug, setActiveBoutiqueSlug] = useState(initial.boutiqueSlug || null);
  const [activeEditionToken, setActiveEditionToken] = useState(initial.editionToken || null);
  const [activeWeddingSlug, setActiveWeddingSlug] = useState(initial.weddingSlug || null);
  const [activationToken, setActivationToken] = useState(initial.activationToken || null);
  const [categorySearchQuery, setCategorySearchQuery] = useState(null);
  const [activeMagazineCategoryId, setActiveMagazineCategoryId] = useState(initial.magazineCategoryId || null);
  const [activeMagazineSlug, setActiveMagazineSlug] = useState(initial.magazineSlug || null);
  const [activeEditorialSlug, setActiveEditorialSlug] = useState(initial.editorialSlug || null);
  const [activeEditorialCategoryId, setActiveEditorialCategoryId] = useState(initial.editorialCategoryId || null);
  const [activeShopProductId, setActiveShopProductId] = useState(initial.shopProductId || null);
  const [activeSearchQuery, setActiveSearchQuery] = useState(initial.searchQuery || null);
  const [shopDepartment, setShopDepartment] = useState(initial.shopDepartment || null);
  const [shopCategory,   setShopCategory]   = useState(initial.shopCategory   || null);
  const [shopSubtype,    setShopSubtype]    = useState(initial.shopSubtype    || null);
  const [shopBrand,    setShopBrand]    = useState(initial.shopBrand    || null);
  const [shopRetailer, setShopRetailer] = useState(initial.shopRetailer || null);
  const [activePublicationsSlug, setActivePublicationsSlug] = useState(initial.publicationsSlug || null);
  const [activeEmbedSlug,        setActiveEmbedSlug]        = useState(initial.publicationsEmbedSlug || null);
  const [activeMagazineIssueSlug, setActiveMagazineIssueSlug] = useState(initial.magazineIssueSlug || null);
  const [activeCollaborateToken,      setActiveCollaborateToken]      = useState(initial.collaborateToken       || null);
  const [activeProofToken,            setActiveProofToken]            = useState(initial.proofToken             || null);
  const [activeVendorAnalyticsToken,  setActiveVendorAnalyticsToken]  = useState(initial.vendorAnalyticsToken   || null);
  const [activeReachIssueSlug,    setActiveReachIssueSlug]    = useState(initial.reachIssueSlug    || null);
  const [activeReachVendorSlug,   setActiveReachVendorSlug]   = useState(initial.reachVendorSlug   || null);
  const [studioThumbnailId,      setStudioThumbnailId]      = useState(initial.templateId || null);
  const [studioEntityType, setStudioEntityType] = useState(initial.entityType || null);
  const [studioSlug, setStudioSlug] = useState(initial.slug || null);
  const [activePsSlug, setActivePsSlug] = useState(initial.psSlug || null);
  const [activeVenueSlug, setActiveVenueSlug] = useState(initial.venueSlug || null);
  const [activeVendorSlug, setActiveVendorSlug] = useState(initial.vendorSlug || null);
  const [activePreviewToken,  setActivePreviewToken]  = useState(initial.previewToken  || null);
  const [activeShowcaseSlug, setActiveShowcaseSlug] = useState(initial.showcaseSlug || null);
  const [activeGuideSlug,   setActiveGuideSlug]   = useState(initial.guideSlug   || null);
  const [activeGuideOrigin, setActiveGuideOrigin] = useState(null);
  const [activeEventSlug, setActiveEventSlug] = useState(initial.eventSlug || null);
  const [activeLocationType, setActiveLocationType] = useState(initial.locationType || null);
  const [activeLocationSlug, setActiveLocationSlug] = useState(initial.locationSlug || null);
  const [activeCitySlug, setActiveCitySlug] = useState(initial.citySlug || null);
  const [activeCommunityChannelSlug,  setActiveCommunityChannelSlug]  = useState(initial.communityChannelSlug  || null);
  const [activeCommunityPostId,       setActiveCommunityPostId]       = useState(initial.communityPostId       || null);
  const [activeCommunityInviteToken,  setActiveCommunityInviteToken]  = useState(initial.inviteToken           || null);
  const [activeCommunityMemberId,     setActiveCommunityMemberId]     = useState(initial.memberId              || null);
  const [activeLearningSeriesSlug,    setActiveLearningSeriesSlug]    = useState(initial.learningSeriesSlug    || null);
  const [activeLearningArticleSlug,   setActiveLearningArticleSlug]   = useState(initial.learningArticleSlug   || null);
  const [magazineLight, setMagazineLight] = useState(true);
  const [needsRedirect, setNeedsRedirect] = useState(initial.redirectToCanonical ? stateToPath(initial.page, initial) : null);


  // Ref: skip pushState when change came from popstate (back/forward)
  const skipPush = useRef(false);

  // Scroll to top on every route change, skip hash/anchor links
  useEffect(() => {
    if (window.location.hash) return; // anchor link, let browser handle native scroll
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }, [
    page,
    activeCountrySlug, activeRegionSlug, activeCategorySlug,
    activePlannerSlug, activeWeddingSlug,
    activeMagazineCategoryId, activeMagazineSlug, activePublicationsSlug, activeMagazineIssueSlug,
    activeEditorialSlug, activeEditorialCategoryId,
    activeVenueSlug, activeVendorSlug, activeShowcaseSlug,
    activeEventSlug, activeLocationType, activeLocationSlug,
  ]);

  // ── Tracker: init on mount, fire page_view on every navigation ───────────
  const trackerInitRef = useRef(false);
  useEffect(() => {
    if (!trackerInitRef.current) {
      trackerInitRef.current = true;
      initTracker();   // fires first page_view + starts heartbeat
    } else {
      trackPageView(); // subsequent navigations
    }
  }, [page, activeCountrySlug, activeRegionSlug, activeCategorySlug, activePlannerSlug, activeWeddingSlug, activeMagazineCategoryId, activeMagazineSlug, activeVenueSlug, activeVendorSlug, activeShowcaseSlug, activeEventSlug, activeLocationType, activeLocationSlug]);

  // ── URL sync: push URL whenever state changes ─────────────────────────────
  useEffect(() => {
    if (skipPush.current) { skipPush.current = false; return; }
    const path = stateToPath(page, {
      psSlug: activePsSlug,
      countrySlug: activeCountrySlug,
      regionSlug: activeRegionSlug,
      categorySlug: activeCategorySlug,
      plannerSlug: activePlannerSlug,
      boutiqueSlug: activeBoutiqueSlug,
      editionToken: activeEditionToken,
      weddingSlug: activeWeddingSlug,
      activationToken: activationToken,
      magazineCategoryId: activeMagazineCategoryId,
      magazineSlug: activeMagazineSlug,
      editorialSlug: activeEditorialSlug,
      editorialCategoryId: activeEditorialCategoryId,
      shopProductId: activeShopProductId,
      searchQuery: activeSearchQuery,
      shopDepartment: shopDepartment,
      shopCategory:   shopCategory,
      shopSubtype:    shopSubtype,
      shopBrand:    shopBrand,
      shopRetailer: shopRetailer,
      publicationsSlug: activePublicationsSlug,
      magazineIssueSlug: activeMagazineIssueSlug,
      collaborateToken: activeCollaborateToken,
      vendorAnalyticsToken: activeVendorAnalyticsToken,
      reachIssueSlug: activeReachIssueSlug,
      reachVendorSlug: activeReachVendorSlug,
      venueSlug: activeVenueSlug,
      vendorSlug: activeVendorSlug,
      showcaseSlug: activeShowcaseSlug,
      guideSlug:    activeGuideSlug,
      eventSlug: activeEventSlug,
      locationType: activeLocationType,
      locationSlug: activeLocationSlug,
      communityChannelSlug: activeCommunityChannelSlug,
      communityPostId:      activeCommunityPostId,
      learningSeriesSlug:   activeLearningSeriesSlug,
      learningArticleSlug:  activeLearningArticleSlug,
      previewToken:         activePreviewToken,
    });
    if (path && window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
  }, [page, activePsSlug, activeCountrySlug, activeRegionSlug, activeCategorySlug, activePlannerSlug, activeWeddingSlug, activationToken, activeMagazineCategoryId, activeMagazineSlug, activeEditorialSlug, activeEditorialCategoryId, activeShopProductId, activeSearchQuery, shopDepartment, shopCategory, shopSubtype, shopBrand, shopRetailer, activePublicationsSlug, activeMagazineIssueSlug, activeVenueSlug, activeVendorSlug, activeShowcaseSlug, activeGuideSlug, activeEventSlug, activeLocationType, activeLocationSlug, activeCommunityChannelSlug, activeCommunityPostId, activeLearningSeriesSlug, activeLearningArticleSlug, activePreviewToken]);

  // ── Redirect to canonical URL if needed ────────────────────────────────────
  useEffect(() => {
    if (needsRedirect && window.location.pathname !== needsRedirect) {
      window.location.replace(needsRedirect);
    }
  }, [needsRedirect]);

  // ── Load active homepage variant from platform_settings ─────────────────
  useEffect(() => {
    supabase
      .from("platform_settings")
      .select("value")
      .eq("key", "active_homepage")
      .maybeSingle()
      .then(({ data }) => { if (data?.value) setActiveHomepage(data.value); })
      .catch(() => {});
  }, []);

  // ── Clear preview session when navigating away from the sealed review ────────
  // preview_session is set in sessionStorage when a user enters a /preview/<token>
  // page. Without this cleanup it would persist for the entire browser session,
  // causing any listing page visited afterwards (cards, quick-view, direct URLs)
  // to render in sealed-review mode via isSealedPreview / SiteModeGate.
  useEffect(() => {
    if (page !== 'preview') {
      try { sessionStorage.removeItem('preview_session'); } catch {}
    }
  }, [page]);

  // ── Popstate: back / forward browser buttons ─────────────────────────────
  useEffect(() => {
    const onPop = () => {
      const s = pathToState(window.location.pathname);
      skipPush.current = true;
      setActiveCountrySlug(s.countrySlug || null);
      setActiveRegionSlug(s.regionSlug || null);
      setActiveCategorySlug(s.categorySlug || null);
      setActivePlannerSlug(s.plannerSlug || null);
      setActiveBoutiqueSlug(s.boutiqueSlug || null);
      setActiveEditionToken(s.editionToken || null);
      setActiveWeddingSlug(s.weddingSlug || null);
      setActivationToken(s.activationToken || null);
      setActiveMagazineCategoryId(s.magazineCategoryId || null);
      setActiveMagazineSlug(s.magazineSlug || null);
      setActiveEditorialSlug(s.editorialSlug || null);
      setActiveEditorialCategoryId(s.editorialCategoryId || null);
      setActiveShopProductId(s.shopProductId || null);
      setActiveSearchQuery(s.searchQuery || null);
      setShopDepartment(s.shopDepartment || null);
      setShopCategory(s.shopCategory || null);
      setShopSubtype(s.shopSubtype || null);
      setShopBrand(s.shopBrand || null);
      setShopRetailer(s.shopRetailer || null);
      setActiveMagazineIssueSlug(s.magazineIssueSlug || null);
      setActivePublicationsSlug(s.publicationsSlug || null);
      setActivePsSlug(s.psSlug || null);
      setActiveVenueSlug(s.venueSlug || null);
      setActiveVendorSlug(s.vendorSlug || null);
      setActivePreviewToken(s.previewToken || null);
      setActiveShowcaseSlug(s.showcaseSlug || null);
      setActiveGuideSlug(s.guideSlug || null);
      setActiveEventSlug(s.eventSlug || null);
      setActiveLocationType(s.locationType || null);
      setActiveLocationSlug(s.locationSlug || null);
      setActiveCitySlug(s.citySlug || null);
      setActiveCommunityChannelSlug(s.communityChannelSlug || null);
      setActiveCommunityPostId(s.communityPostId || null);
      setActiveCommunityInviteToken(s.inviteToken || null);
      setActiveCommunityMemberId(s.memberId || null);
      setActiveLearningSeriesSlug(s.learningSeriesSlug || null);
      setActiveLearningArticleSlug(s.learningArticleSlug || null);
      setStudioThumbnailId(s.templateId || null);
      setCategoryRegion(null);
      setCategorySearchQuery(null);
      setPage(s.page);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── Anchor interception: make <a href> an SPA navigation ─────────────────────
  // Public pages ship real <a href> links (49 on the homepage) so crawlers, AI
  // fetchers and keyboard users can follow them. Nothing intercepted their clicks,
  // so each one was a full document navigation: the SPA was torn down, the server's
  // HTML was painted, then createRoot() discarded it and rebuilt the page.
  // installLinkInterceptor keeps the href and cancels only the browser's default.
  // Server-handled paths (/assets/*, /feed.xml) and the 301s in public/_redirects
  // are deliberately NOT intercepted, see src/utils/navigate.js.
  useEffect(() => installLinkInterceptor(), []);

  // ── View Transitions helper ──────────────────────────────────────────────────
  // Wraps any state-change fn in the View Transitions API for a native cross-fade.
  // Falls back silently on browsers that don't support it (Firefox, older Safari).
  const vt = (fn) => {
    if (typeof document !== "undefined" && document.startViewTransition) {
      document.startViewTransition(() => { fn(); });
    } else {
      fn();
    }
  };

  const goHome = () => vt(() => {
    setCategoryRegion(null); setActiveCountrySlug(null); setActiveRegionSlug(null);
    setActiveCategorySlug(null); setCategorySearchQuery(null);
    setPage("root");
  });
  const goSearch = (q) => vt(() => { setActiveSearchQuery(q || ""); setPage("search"); });
  const goVenue = (venueOrSlug) => {
    setCategoryRegion(null); setCategorySearchQuery(null);
    const isObj = typeof venueOrSlug === 'object' && venueOrSlug !== null;
    const slug  = isObj ? venueOrSlug.slug        : venueOrSlug;
    const cs    = isObj ? (venueOrSlug.countrySlug  || venueOrSlug.country_slug  || null) : null;
    const rs    = isObj ? (venueOrSlug.regionSlug   || venueOrSlug.region_slug   || null) : null;
    // Normalise: 'boutique' → 'bridal-boutiques', 'caterer' → 'caterers' etc.
    const rawCat = isObj ? (venueOrSlug.categorySlug || venueOrSlug.category_slug || 'wedding-venues') : null;
    const cat    = rawCat ? getCanonicalCategorySlug(rawCat) : null;
    setActiveCountrySlug(cs);
    setActiveRegionSlug(rs);
    setActiveCategorySlug(cat);
    if (slug) {
      setActiveVenueSlug(slug);
      setPage("listing-profile");
    } else {
      // Guard: no slug = no navigation. Static fallback cards without a slug
      // must never route home. Log so we can identify the broken entry.
      console.warn('[goVenue] card has no slug, navigation blocked', venueOrSlug);
    }
  };
  const goRegion = (countrySlug, regionSlug) => vt(() => {
    setActiveCountrySlug(countrySlug || null);
    setActiveRegionSlug(regionSlug || null);
    setCategoryRegion(null);
    setActiveCategorySlug(null); setCategorySearchQuery(null);
    setPage("region");
  });
  const goRegionCategory = (countrySlug, regionSlug, categorySlug) => vt(() => {
    setActiveCountrySlug(countrySlug || null);
    setActiveRegionSlug(regionSlug || null);
    setActiveCategorySlug(categorySlug || null);
    setCategoryRegion(null); setCategorySearchQuery(null);
    setPage("region-category");
  });
  const goCategory = (filterCtx) => {
    // NEW: Support Phase 1 URL routing for categories
    // When passed { category, countrySlug, regionSlug }, navigate to new URL pattern
    if (filterCtx && typeof filterCtx === "object" && filterCtx.category) {
      const catSlug = filterCtx.category;

      // "wedding-vendors" is the umbrella vendor directory, not a category page
      if (catSlug === "wedding-vendors") {
        setCategoryRegion(null); setCategorySearchQuery(null);
        setPage("vendor-directory");
        return;
      }

      const ctrySlug = filterCtx.countrySlug || null;
      const regSlug = filterCtx.regionSlug || null;

      setActiveCountrySlug(ctrySlug);
      setActiveRegionSlug(regSlug);
      setActiveCategorySlug(catSlug);
      setCategoryRegion(null);
      setCategorySearchQuery(null);
      setPage("region-category");
      return;
    }

    // OLD: Legacy category mode (backward compatibility)
    if (typeof filterCtx === "string") {
      setCategoryRegion(filterCtx || null);
      setCategorySearchQuery(null);
    } else if (filterCtx && typeof filterCtx === "object") {
      setCategoryRegion(filterCtx.regionSlug || null);
      setActiveCountrySlug(filterCtx.countrySlug || null);
      setActiveRegionSlug(filterCtx.regionSlug || null);
      setCategorySearchQuery(filterCtx.searchQuery || null);
    } else {
      setCategoryRegion(null);
      setCategorySearchQuery(null);
    }
    setPage("category");
  };
  const goPlannerProfile = (countrySlug, regionSlug, planner) => {
    setActiveCountrySlug(countrySlug || null);
    setActiveRegionSlug(regionSlug || null);
    setActiveCategorySlug("wedding-planners");
    setActivePlannerSlug(toSlug(planner.name));
    setPage("planner-profile");
  };
  const goStandard = () => setPage("standard");
  const goAbout       = () => setPage("about");
  const goContact     = () => setPage("contact");
  const goAllDestinations = () => setPage("all-destinations");
  const goRegionGuide   = (slug) => vt(() => { setActiveGuideSlug(slug); setActiveGuideOrigin(null); setPage("region-guide"); });
  const goRegionGuideFromAtlas = (slug) => vt(() => { setActiveGuideSlug(slug); setActiveGuideOrigin("wedding-atlas"); setPage("region-guide"); });
  const goWeddingAtlas  = () => vt(() => setPage("wedding-atlas"));
  const goAdvisory    = () => setPage("advisory");
  const goOurApproach = () => setPage("our-approach");
  const goPartnership = () => setPage("partnership-home");
  const goPartnerEnquiry = () => setPage("partner-enquiry");
  const goUSA         = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("usa"); };
  const goItaly       = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("italy"); };
  const goCountry     = (slug) => vt(() => { setActiveLocationType("country"); setActiveLocationSlug(slug); setActiveCountrySlug(slug); setActiveRegionSlug(null); setActiveCategorySlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("location"); });
  const goAdmin       = () => setPage("admin");
  const goCommunityFeed     = () => { setActiveCommunityChannelSlug(null); setActiveCommunityPostId(null); setPage("community-feed"); };
  const goCommunityLearning = () => { setActiveLearningSeriesSlug(null); setActiveLearningArticleSlug(null); setPage("community-learning"); };
  const goCommunityChannel  = (slug) => { setActiveCommunityChannelSlug(slug || null); setActiveCommunityPostId(null); setPage("community-channel"); };
  const goCommunityPost     = (id)   => { setActiveCommunityPostId(id || null);        setPage("community-post"); };
  const goCommunityLearningSeries  = (slug) => { setActiveLearningSeriesSlug(slug || null); setActiveLearningArticleSlug(null); setPage("community-learning-series"); };
  const goCommunityLearningArticle = (slug, serSlug) => { setActiveLearningArticleSlug(slug || null); setActiveLearningSeriesSlug(serSlug || null); setPage("community-learning-article"); };
  const goVendor              = () => setPage("vendor");
  const goVendorPublic        = (vendorOrSlug) => {
    // When given a full object (from DB), use goVenue for canonical URL + VenueProfile.
    // When given only a slug string (legacy), fall back to VendorPublicPage.
    if (vendorOrSlug && typeof vendorOrSlug === "object") {
      goVenue(vendorOrSlug);
      return;
    }
    const slug = vendorOrSlug;
    if (!slug) return;
    vt(() => {
      setActiveVendorSlug(slug);
      setPage("vendor-public");
    });
  };
  const goPortal              = () => setPage("portal");
  const goVendorLogin         = () => setPage("vendor-login");
  const goVendorSignup        = () => setPage("vendor-signup");
  const goVendorConfirmEmail  = () => setPage("vendor-confirm-email");
  const goVendorForgotPassword = () => setPage("vendor-forgot-password");
  const goVendorResetPassword  = () => setPage("vendor-reset-password");
  const goVendorActivate      = (token) => { setActivationToken(token || null); setPage("vendor-activate"); };
  const goCoupleSignup        = () => setPage("couple-signup");
  const goCoupleLogin         = () => setPage("couple-login");
  const goCoupleConfirmEmail  = () => setPage("couple-confirm-email");
  const goCoupleForgotPassword = () => setPage("couple-forgot-password");
  const goCoupleResetPassword  = () => setPage("couple-reset-password");
  const goAdminLogin  = () => setPage("admin-login");
  const goAuraDiscovery    = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("aura-discovery"); };
  const goDiscoverWithAura = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("discover-with-aura"); };
  const goAuraForVenues    = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("aura-for-venues"); };
  const goAuraForVendors   = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("aura-for-vendors"); };
  const goEventDetail = (eventSlug) => { setActiveEventSlug(eventSlug); setPage("event-detail"); };

  // Store couple/vendor nav functions on window for use in auth components
  useEffect(() => {
    window.coupleGoSignup = goCoupleSignup;
    window.coupleGoLogin = goCoupleLogin;
    window.vendorGoSignup = goVendorSignup;
    window.vendorGoLogin = goVendorLogin;
    window.goJoin = goJoin;
  }, []);
  const goRealWeddings = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("real-weddings"); };
  const goRealWeddingDetail = (weddingSlug) => { setActiveWeddingSlug(weddingSlug); setPage("real-wedding-detail"); };
  const goShortlist = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("shortlist"); };
  const goGettingMarried = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("getting-married"); };
  const goArtistryAwards = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("artistry-awards"); };
  const goJoin = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("join"); };
  const goApplyForInclusion = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("apply-for-inclusion"); };
  const goPricing = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("pricing"); };
  const goListings = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("listings"); };
  const goH2 = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("h2"); };
  const goH3 = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("h3"); };
  const goAdvertise = () => { setActiveCountrySlug(null); setActiveRegionSlug(null); setActiveCategorySlug(null); setActivePlannerSlug(null); setActiveWeddingSlug(null); setCategoryRegion(null); setCategorySearchQuery(null); setPage("advertise"); };
  const goMagazine = () => { setActiveMagazineCategoryId(null); setActiveMagazineSlug(null); setPage("magazine"); };
  const goMagazineCategory = (categoryId) => { setActiveMagazineCategoryId(categoryId); setActiveMagazineSlug(null); setPage("magazine-category"); };
  const goMagazineArticle = (slug) => { setActiveMagazineSlug(slug); setActiveMagazineCategoryId(null); setPage("magazine-article"); };
  const goPublications      = () => { setActivePublicationsSlug(null); setPage("publications"); };
  const goPublicationsReader = (slug) => { setActivePublicationsSlug(slug); setPage("publications-reader"); };
  const goMagazineReader     = (slug) => { setActiveMagazineIssueSlug(slug); setPage("magazine-reader"); };
  const goPublicationsEmbed  = (slug) => { setActiveEmbedSlug(slug); setPage("publications-embed"); };
  const goPublicationStudio  = () => { setPage("publication-studio"); };
  const goSocialCanvas       = () => { setPage("social-canvas"); };
  const goMagazineFashion = () => { setActiveMagazineSlug(null); setActiveMagazineCategoryId(null); setPage("magazine-fashion"); };
  const goShop = (department, category, subtype, brand, retailer) => {
    setShopDepartment(department || null);
    setShopCategory(category || null);
    setShopSubtype(subtype || null);
    setShopBrand(brand || null);
    setShopRetailer(retailer || null);
    setActiveShopProductId(null);
    setPage("shop");
  };
  const goShopProduct = (id, slug) => {
    // Always store the UUID, never the slug, so ShopProductPage can load by id.
    // The slug is used only for the URL: ShopProductPage calls history.replaceState
    // to the clean slug after the product loads.
    setActiveShopProductId(id);
    setPage("shop-product");
  };
  const goEditorial         = () => { setActiveEditorialSlug(null); setActiveEditorialCategoryId(null); setPage("editorial"); };
  // Centralised "category click" handler. A null catId means the user
  // hit the THE EDIT wordmark or the ALL link in the category nav,
  // route them to /the-edit (M2 homepage) instead of the legacy
  // editorial-category page state, which still mounts the classic
  // EditorialHomePage and looked like the old site to the user.
  const goEditorialArticle  = (slug) => { setActiveEditorialSlug(slug); setActiveEditorialCategoryId(null); setPage("editorial-article"); };
  const goEditorialCategory = (catId) => {
    if (catId == null) { goEditorial(); return; }
    setActiveEditorialCategoryId(catId);
    setActiveEditorialSlug(null);
    setPage("editorial-category");
  };
  const [magazineEditSlug, setMagazineEditSlug] = useState(null);
  const goMagazineStudio  = (slug) => {
    if (slug) setMagazineEditSlug(slug);
    setPage("magazine-studio");
  };

  // ── Centralized footer navigation (passed to every page for SiteFooter) ───
  const footerNav = {
    onNavigateHome: goHome,
    onNavigateContact: goContact,
    onNavigatePartnership: goPartnership,
    onNavigateAdmin: goAdmin,
    onNavigateAbout: goAbout,
    onNavigateStandard: goStandard,
    onViewCategory: goCategory,
    onNavigateGettingMarried: goGettingMarried,
    onNavigateArtistryAwards: goArtistryAwards,
    onNavigateMagazine: goMagazine,
    onNavigatePartnerEnquiry: goPartnerEnquiry,
    onNavigatePricing: goPricing,
  };

  return (
    <AdminAuthProvider>
      <VendorAuthProvider>
        <CoupleAuthProvider>
          <ShortlistProvider>
            <AuraProvider>
            <FloatingUiManager>

        {/* ── Site Mode gate, component lives inside providers so hooks work ── */}
        <SiteModeGate page={page} />

        {/* ── Pages ── */}
        {/* /venue removed, dead route with no data */}
        {page === "venue-profile" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <VenueProfile key={activeVenueSlug} slug={activeVenueSlug} onBack={goHome} />
          </Suspense>
        )}
        {page === "listing-profile" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <VenueProfile
              key={activeVenueSlug}
              slug={activeVenueSlug}
              countrySlug={activeCountrySlug}
              regionSlug={activeRegionSlug}
              categorySlug={activeCategorySlug}
              onBack={goHome}
            />
          </Suspense>
        )}
        {page === "venue-reviews" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <VenueReviewsPage slug={activeVenueSlug} categorySlug={activeCategorySlug} />
          </Suspense>
        )}
        {page === "event-detail" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <EventDetailPage slug={activeEventSlug} onBack={goHome} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "event-review" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <EventReviewPage />
          </Suspense>
        )}
        {page === "dde-showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShowcasePage
              slug="domaine-des-etangs"
              darkMode={darkMode}
              onToggleDark={toggleDark}
              onBack={() => { setActiveVenueSlug("domaine-des-etangs"); setPage("venue-profile"); }}
              onNavigateHome={goHome}
              onGoDestination={(countrySlug) => {
                if (countrySlug) { setActiveCountrySlug(countrySlug); setPage("italy"); }
                else { setPage("root"); }
              }}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "gt-showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShowcasePage
              slug="grand-tirolia-kitzbuehel"
              darkMode={darkMode}
              onToggleDark={toggleDark}
              onBack={() => { setActiveVenueSlug("grand-tirolia-kitzbuehel"); setPage("venue-profile"); }}
              onNavigateHome={goHome}
              onGoDestination={(countrySlug) => {
                if (countrySlug) { setActiveCountrySlug(countrySlug); setPage("root"); }
                else { setPage("root"); }
              }}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "sskrabey-showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShowcasePage
              slug="six-senses-krabey-island"
              darkMode={darkMode}
              onToggleDark={toggleDark}
              onBack={() => { setActiveVenueSlug("six-senses-krabey-island"); setPage("venue-profile"); }}
              onNavigateHome={goHome}
              onGoDestination={(countrySlug) => {
                if (countrySlug) { setActiveCountrySlug(countrySlug); setPage("italy"); }
                else { setPage("root"); }
              }}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "ritz-showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShowcasePage
              slug="the-ritz-london"
              darkMode={darkMode}
              onToggleDark={toggleDark}
              onBack={() => { setActiveVenueSlug("the-ritz-london"); setPage("venue-profile"); }}
              onNavigateHome={goHome}
              onGoDestination={(countrySlug) => {
                if (countrySlug) { setActiveCountrySlug(countrySlug); setPage("italy"); }
                else { setPage("root"); }
              }}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "ic-park-lane-showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShowcasePage
              slug="intercontinental-london-park-lane"
              darkMode={darkMode}
              onToggleDark={toggleDark}
              onBack={() => { setActiveVenueSlug("intercontinental-london-park-lane"); setPage("venue-profile"); }}
              onNavigateHome={goHome}
              onGoDestination={(countrySlug) => {
                if (countrySlug) { setActiveCountrySlug(countrySlug); setPage("location"); }
                else { setPage("root"); }
              }}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "preview" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0d0f12" }} />}>
            <PreviewPage token={activePreviewToken} />
          </Suspense>
        )}
        {page === "showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShowcasePage
              key={activeShowcaseSlug}
              slug={activeShowcaseSlug}
              darkMode={darkMode}
              onToggleDark={toggleDark}
              onBack={() => {
                setActiveVenueSlug(activeShowcaseSlug);
                setPage("venue-profile");
              }}
              onNavigateHome={goHome}
              onGoDestination={(countrySlug) => {
                if (countrySlug) {
                  setActiveCountrySlug(countrySlug);
                  setPage("italy"); // expand later per country
                } else {
                  setPage("root");
                }
              }}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {/* OPTION A: Dynamic location page (country/region/city from Supabase) */}
        {page === "location" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <LocationPage
              locationType={activeLocationType}
              locationSlug={activeLocationSlug}
              onBack={goHome}
              onViewVenue={goVenue}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              onViewCountry={goCountry}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "region" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <RegionPage onBack={goHome} onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} onViewCountry={goCountry} countrySlug={activeCountrySlug} regionSlug={activeRegionSlug} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "planner-profile" && (() => {
          const currentPlanner = getPlannerByIdOrSlug(activePlannerSlug);
          const similarPlanners = VENDORS.filter(
            (v) => v.category === "planner" && v.id !== currentPlanner?.id && v.countrySlug === activeCountrySlug
          ).slice(0, 6);
          return (
            <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
              <PlannerProfilePage
                plannerSlug={activePlannerSlug}
                getPlannerByIdOrSlug={getPlannerByIdOrSlug}
                onBack={() => goRegionCategory(activeCountrySlug, activeRegionSlug, "wedding-planners")}
                onOpenChat={() => {}}
                onSave={() => {}}
                onViewPlanner={(planner) => goVenue(planner)}
                similarPlanners={similarPlanners}
                countrySlug={activeCountrySlug}
                regionSlug={activeRegionSlug}
                onViewRegion={goRegion}
                onViewRegionCategory={goRegionCategory}
                footerNav={footerNav}
              />
            </Suspense>
          );
        })()}
        {page === "artistry-awards" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ArtistryPage
              onNavigateStandard={goHome}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "publications" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <PublicationsPage
              onRead={goPublicationsReader}
              onBack={goHome}
              footerNav={footerNav}
              onToggleDark={toggleDark}
              darkMode={darkMode}
              onVendorLogin={goVendorLogin}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "publications-reader" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <ReaderErrorBoundary onBack={goPublications}>
              <PublicationsReaderPage
                slug={activePublicationsSlug}
                onBack={goPublications}
              />
            </ReaderErrorBoundary>
          </Suspense>
        )}
        {page === "magazine-reader" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <ReaderErrorBoundary onBack={() => setPage("publication-studio")}>
              <PublicationsReaderPage
                slug={activeMagazineIssueSlug}
                onBack={() => setPage("publication-studio")}
                isPreview={true}
              />
            </ReaderErrorBoundary>
          </Suspense>
        )}
        {page === "publications-embed" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <PublicationsEmbedPage slug={activeEmbedSlug} />
          </Suspense>
        )}
        {page === "publication-studio" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <PublicationStudio
              onBack={goHome}
              onReadIssue={goMagazineReader}
            />
          </Suspense>
        )}
        {page === "social-canvas" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <SocialCanvas onBack={goHome} />
          </Suspense>
        )}
        {page === "magazine-collaborate" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <MagazineCollaboratePage
              token={activeCollaborateToken}
              onBack={() => setPage("publication-studio")}
            />
          </Suspense>
        )}
        {page === "magazine-proof-approval" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorProofApprovalPage
              token={activeProofToken}
              onBack={() => setPage("root")}
            />
          </Suspense>
        )}
        {page === "magazine-vendor-analytics" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <MagazineVendorAnalyticsPage
              token={activeVendorAnalyticsToken}
            />
          </Suspense>
        )}
        {page === "magazine-reach" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorReachPage
              issueSlug={activeReachIssueSlug}
              vendorSlug={activeReachVendorSlug}
              onBack={() => setPage("publication-studio")}
            />
          </Suspense>
        )}
        {page === "studio-thumbnail" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <ThumbnailRenderer templateId={studioThumbnailId} />
          </Suspense>
        )}
        {page === "magazine" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <MagazineHomePage
              onNavigateArticle={goMagazineArticle}
              onNavigateCategory={goMagazineCategory}
              onNavigateFashion={goMagazineFashion}
              onEdit={goMagazineStudio}
              isLight={magazineLight}
              onToggleLight={() => setMagazineLight(l => !l)}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "magazine-fashion" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <FashionLandingPage
              onNavigateArticle={goMagazineArticle}
              onNavigateCategory={goMagazineCategory}
              onNavigateHome={goMagazine}
              isLight={magazineLight}
              onToggleLight={() => setMagazineLight(l => !l)}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "magazine-category" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CategoryPage
              categoryId={activeMagazineCategoryId}
              onNavigateArticle={goMagazineArticle}
              onNavigateHome={goMagazine}
              onNavigateCategory={goMagazineCategory}
              onEdit={goMagazineStudio}
              isLight={magazineLight}
              onToggleLight={() => setMagazineLight(l => !l)}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "magazine-preview" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <MagazinePreviewPage />
          </Suspense>
        )}
        {page === "magazine-article" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <MagazineArticlePage
              slug={activeMagazineSlug}
              onNavigateArticle={goMagazineArticle}
              onNavigateHome={goMagazine}
              onNavigateCategory={goMagazineCategory}
              onNavigateFashion={goMagazineFashion}
              onEdit={goMagazineStudio}
              isLight={magazineLight}
              onToggleLight={() => setMagazineLight(l => !l)}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "magazine-studio" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0B0906", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading-primary,'Gilda Display',serif)", fontSize: 28, letterSpacing: "0.15em", color: "#d39a21" }}>Loading LWD</div>}>
            <MagazineStudio
              onNavigateMagazine={goMagazine}
              onNavigateHome={goHome}
              editSlug={magazineEditSlug}
            />
          </Suspense>
        )}
        {page === "studio-edit" && (
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Loading editor...</div>}>
            <UniversalStudioRouter entityType={studioEntityType} slug={studioSlug} />
          </Suspense>
        )}
        {page === "shop" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: darkMode ? "#0c0b09" : "#faf8f4" }} />}>
            <ShopPage
              onNavigateHome={goHome}
              onNavigateProduct={goShopProduct}
              onNavigateShopFilter={(department, category, subtype, brand, retailer) => goShop(department, category, subtype, brand, retailer)}
              initialDepartment={shopDepartment}
              initialCategory={shopCategory}
              initialSubtype={shopSubtype}
              initialBrand={shopBrand}
              initialRetailer={shopRetailer}
              footerNav={footerNav}
              darkMode={darkMode}
              onToggleDark={toggleDark}
            />
          </Suspense>
        )}
        {page === "shop-product" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: darkMode ? "#0c0b09" : "#faf8f4" }} />}>
            <ShopProductPage
              productId={activeShopProductId}
              onNavigateHome={goHome}
              onNavigateShop={goShop}
              onNavigateProduct={goShopProduct}
              footerNav={footerNav}
              darkMode={darkMode}
              onToggleDark={toggleDark}
            />
          </Suspense>
        )}
        {/* /the-edit now renders the M2 (admin-configured) layout. The
            classic EditorialHomePage component is kept in the codebase
            for fallback / rollback, but is no longer wired here. */}
        {page === "editorial" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#f6f2ea" }} />}>
            <LWDEditHome
              onNavigateArticle={goEditorialArticle}
              onNavigateCategory={goEditorialCategory}
              onNavigateHome={goHome}
            />
          </Suspense>
        )}
        {page === "editorial-m2" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <EditorialMagazineHome
              onNavigateArticle={goEditorialArticle}
              onNavigateCategory={goEditorialCategory}
              onNavigateHome={goHome}
            />
          </Suspense>
        )}
        {page === "editorial-article" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#070604" }} />}>
            <EditorialArticlePage
              slug={activeEditorialSlug}
              onNavigateArticle={goEditorialArticle}
              onNavigateHome={goEditorial}
              onNavigateCategory={goEditorialCategory}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "editorial-category" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#070604" }} />}>
            <EditorialHomePage
              categoryId={activeEditorialCategoryId}
              onNavigateArticle={goEditorialArticle}
              onNavigateCategory={goEditorialCategory}
              onNavigateHome={goHome}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "aura-discovery" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <AuraDiscoveryDemoPage onViewVenue={goVenue} />
          </Suspense>
        )}
        {page === "aura" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0D0B09" }} />}>
            <AuraPage onNavigateHome={goHome} />
          </Suspense>
        )}
        {page === "discover-with-aura" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FFFBF3" }} />}>
            <DiscoverWithAuraPage onNavigateHome={goHome} darkMode={darkMode} onToggleDark={toggleDark} />
          </Suspense>
        )}
        {page === "aura-for-venues" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAFAF8" }} />}>
            <AuraForVenuesPage onNavigateHome={goHome} darkMode={darkMode} onToggleDark={toggleDark} />
          </Suspense>
        )}
        {page === "aura-for-vendors" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FFFBF7" }} />}>
            <AuraForVendorsPage onNavigateHome={goHome} darkMode={darkMode} onToggleDark={toggleDark} />
          </Suspense>
        )}
        {page === "luxury-region-test" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <LuxuryRegionPageTest countrySlug="italy" regionSlug="amalfi-coast" />
          </Suspense>
        )}
        {page === "grid-responsive-test" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <GridResponsiveTest />
          </Suspense>
        )}
        {page === "advisory" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <AdvisoryPage
              onNavigateHome={goHome}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
              onNavigateApproach={goOurApproach}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "our-approach" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <OurApproachPage
              onNavigateHome={goHome}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
              onNavigateAdvisory={goAdvisory}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {/* /community, intelligence landing page */}
        {page === "community" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CommunityIntelligencePage onNavigate={(pg, opts) => {
              if (pg === "community-member") { setActiveCommunityMemberId(opts?.memberId || null); setPage("community-member"); }
              else setPage(pg);
            }} />
          </Suspense>
        )}
        {/* /community/join/:token, invitation acceptance + member onboarding */}
        {page === "community-join" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CommunityJoinPage
              token={activeCommunityInviteToken}
              onNavigate={(pg, opts) => {
                if (pg === "community-member") { setActiveCommunityMemberId(opts?.memberId || null); setPage("community-member"); }
                else setPage(pg);
              }}
            />
          </Suspense>
        )}
        {/* /community/members/:id, member profile */}
        {page === "community-member" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CommunityMemberPage
              memberId={activeCommunityMemberId}
              onNavigate={(pg, opts) => {
                if (pg === "community-member") { setActiveCommunityMemberId(opts?.memberId || null); setPage("community-member"); }
                else setPage(pg);
              }}
            />
          </Suspense>
        )}
        {/* /community/feed, /community/c/:slug, /community/p/:id */}
        {(page === "community-feed" || page === "community-learning" || page === "community-learning-series" || page === "community-learning-article" || page === "community-channel" || page === "community-post") && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CommunityPage
              view={
                page === "community-post"              ? "post"
                : page === "community-learning"          ? "learning"
                : page === "community-learning-series"   ? "learning-series"
                : page === "community-learning-article"  ? "learning-article"
                : page === "community-channel"           ? "channel"
                : "feed"
              }
              channelSlug={activeCommunityChannelSlug}
              postId={activeCommunityPostId}
              learningSeriesSlug={activeLearningSeriesSlug}
              learningArticleSlug={activeLearningArticleSlug}
              onNavigateHome={goHome}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
              onNavigateFeed={goCommunityFeed}
              onNavigateChannel={goCommunityChannel}
              onNavigatePost={goCommunityPost}
              onNavigateLearning={goCommunityLearning}
              onNavigateLearningSeries={goCommunityLearningSeries}
              onNavigateLearningArticle={goCommunityLearningArticle}
            />
          </Suspense>
        )}
        {page === "amalfi-coast-v2" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <RegionCategoryPage
              onBack={() => goHome()}
              onBackHome={goHome}
              onViewVenue={goVenue}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              onViewVendor={goVendor}
              onViewPlanner={(planner) => goVenue(planner)}
              onViewWedding={goWedding}
              countrySlug="italy"
              regionSlug="amalfi-coast"
              isTestMode={true}
            />
          </Suspense>
        )}
        {page === "all-destinations" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <AllDestinationsPage goCountry={goCountry} goRegion={goRegion} goVenue={goVenue} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "region-category" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <RegionCategoryPage
              onBack={() => goRegion(activeCountrySlug, activeRegionSlug)}
              onBackHome={goHome}
              onViewVenue={goVenue}
              onViewVendor={goVendorPublic}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              onViewCountry={goCountry}
              onViewPlanner={(planner) => goVenue(planner)}
              countrySlug={activeCountrySlug}
              regionSlug={activeRegionSlug}
              categorySlug={activeCategorySlug}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "city-category" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <RegionCategoryPage
              onBack={() => goRegion(activeCountrySlug, activeRegionSlug)}
              onBackHome={goHome}
              onViewVenue={goVenue}
              onViewVendor={goVendorPublic}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              onViewCountry={goCountry}
              onViewPlanner={(planner) => goVenue(planner)}
              countrySlug={activeCountrySlug}
              regionSlug={activeRegionSlug}
              categorySlug={activeCategorySlug}
              citySlug={activeCitySlug}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "category" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ItalyPage noIndex onBack={goHome} onViewVenue={goVenue} onViewRegion={goRegion} onViewCategory={goCategory} initialRegion={categoryRegion} initialSearchQuery={categorySearchQuery} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "standard" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <LWDStandard onBack={goHome} onViewCategory={goCategory} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "coming-soon" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ComingSoonPage />
          </Suspense>
        )}
        {page === "about" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <AboutLWD onBack={goHome} onViewCategory={goCategory} onViewStandard={goStandard} onViewContact={goContact} onViewPartnership={goPartnership} footerNav={footerNav} />
          </Suspense>
        )}
        {/* ── Canonical slug: /about-us ── */}
        {page === "about-us" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <AboutLWD onBack={goHome} onViewCategory={goCategory} onViewStandard={goStandard} onViewContact={goContact} onViewPartnership={goPartnership} footerNav={footerNav} />
          </Suspense>
        )}
        {/* ── /vision, The Vision: positioning page (unlinked until nav approval) ── */}
        {page === "vision" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0a0b0e" }} />}>
            <VisionPage
              onBack={goHome}
              onViewStandard={goStandard}
              onViewAbout={() => { window.history.pushState(null, "", "/about-us"); window.dispatchEvent(new PopStateEvent("popstate")); }}
              onViewContact={goContact}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {/* ── /faq, Answer-engine document for AI Overviews + PAA ── */}
        {page === "faq" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <FaqPage onBack={goHome} />
          </Suspense>
        )}
        {/* ── /press, Entity anchor + media information ── */}
        {page === "press" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <PressPage onBack={goHome} />
          </Suspense>
        )}
        {page === "careers" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CareersPage onViewContact={goContact} />
          </Suspense>
        )}
        {/* ── Region guide pages ── */}
        {page === "region-guide" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <RegionGuidePage
              slug={activeGuideSlug}
              onBack={activeGuideOrigin === "wedding-atlas" ? goWeddingAtlas : goHome}
              onViewListings={(country, region) => goRegionCategory(country, region, "wedding-venues")}
              onNavigateGuide={goRegionGuide}
              onNavigateAtlas={goWeddingAtlas}
              onNavigateAbout={goAbout}
              onNavigateStandard={goStandard}
              onVendorLogin={goVendorLogin}
              darkMode={darkMode}
              onToggleDark={toggleDark}
            />
          </Suspense>
        )}
        {/* ── Wedding Atlas, /wedding-guides ── */}
        {page === "wedding-atlas" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <WeddingAtlasPage
              onNavigateGuide={goRegionGuideFromAtlas}
              onNavigateAbout={goAbout}
              onNavigateStandard={goStandard}
              onVendorLogin={goVendorLogin}
              darkMode={darkMode}
              onToggleDark={toggleDark}
            />
          </Suspense>
        )}
        {page === "taigenic" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <TaigenicPage onBack={goHome} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "contact" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ContactLWD onBack={goHome} onViewCategory={goCategory} onViewStandard={goStandard} onViewAbout={goAbout} onViewPartnership={goPartnership} footerNav={footerNav} />
          </Suspense>
        )}
        {/* ── Canonical slug: /contact-us ── */}
        {page === "contact-us" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ContactLWD onBack={goHome} onViewCategory={goCategory} onViewStandard={goStandard} onViewAbout={goAbout} onViewPartnership={goPartnership} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "privacy" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="privacy" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {/* ── Canonical slug: /privacy-policy ── */}
        {page === "privacy-policy" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="privacy" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {page === "terms" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="terms" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {/* ── Canonical slug: /terms-and-conditions ── */}
        {page === "terms-and-conditions" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="terms" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {/* ── Generic Page Studio renderer, serves any published PS page by slug ── */}
        {page === "ps-page" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <PageStudioRenderer slug={activePsSlug} onBack={goHome} footerNav={footerNav} darkMode={darkMode} onToggleDark={toggleDark} />
          </Suspense>
        )}
        {page === "cookie-policy" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="cookies" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {page === "reviews-policy" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="reviews-policy" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {page === "support" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <CmsPage pageKey="support" onBack={goHome} footerNav={footerNav} darkMode={darkMode} />
          </Suspense>
        )}
        {page === "unsubscribe" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <UnsubscribePage />
          </Suspense>
        )}
        {page === "partnership" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <LWDPartnership onBack={goHome} onViewCategory={goCategory} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} footerNav={footerNav} />
          </Suspense>
        )}
        {/* Country-specific render cases removed.
            All countries now route through page === "location" → LocationPage.
            Supabase locations table is the single source of truth. */}
        {page === "admin-login" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <AdminLogin onBack={goHome} />
          </Suspense>
        )}
        {page === "admin-oauth-callback" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <GoogleOAuthCallback />
          </Suspense>
        )}
        {page === "admin" && (
          <AdminRoute onBack={goHome} onNavigate={(action, data) => {
            if (action === 'magazine-studio') { goMagazineStudio(); return; }
            console.log("Admin navigation:", action, data);
          }} />
        )}
        {page === "editorial-showcase" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <EditorialShowcase />
          </Suspense>
        )}
        {page === "vendor-login" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorLogin onLoginSuccess={(dest) => dest === 'portal' ? goPartnership() : goVendor()} />
          </Suspense>
        )}
        {page === "vendor-signup" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorSignup onSignupSuccess={goVendor} />
          </Suspense>
        )}
        {page === "vendor-activate" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorActivate activationToken={activationToken} onActivationSuccess={goVendorLogin} />
          </Suspense>
        )}
        {page === "vendor-confirm-email" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorConfirmEmail />
          </Suspense>
        )}
        {page === "vendor-forgot-password" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorForgotPassword />
          </Suspense>
        )}
        {page === "vendor-reset-password" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <VendorResetPassword />
          </Suspense>
        )}
        {page === "vendor-public" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <VendorPublicPage vendorSlug={activeVendorSlug} onBack={goHome} darkMode={darkMode} onToggleDark={toggleDark} />
          </Suspense>
        )}
        {page === "vendor" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0B0906", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading-primary,'Gilda Display',serif)", fontSize: 28, letterSpacing: "0.15em", color: "#d39a21" }}>Loading LWD</div>}>
            <VendorDashboard onBack={goHome} onVendorLogin={goVendorLogin} />
          </Suspense>
        )}
        {page === "portal" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906", display: "flex", alignItems: "center", justifyContent: "center", color: "#8a7d6a", fontSize: 13, fontFamily: "Inter,sans-serif", letterSpacing: "2px" }}>Loading your portal...</div>}>
            <ClientPortal />
          </Suspense>
        )}
        {page === "partnership-home" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#1A1610" }} />}>
            <PartnershipHome />
          </Suspense>
        )}
        {page === "intelligence-dashboard" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}>
            <IntelligenceDashboard />
          </Suspense>
        )}
        {page === "intelligence-assessment" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}>
            <IntelligenceAssessment />
          </Suspense>
        )}
        {page === "competitive-watch" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}>
            <CompetitiveWatch />
          </Suspense>
        )}
        {page === "portal-listing" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><ListingScreen /></Suspense>)}
        {page === "portal-reviews" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><ReviewsScreen /></Suspense>)}
        {page === "portal-leads" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><LeadsScreen /></Suspense>)}
        {page === "portal-analytics" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><AnalyticsScreen /></Suspense>)}
        {page === "portal-activity" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><ActivityScreen /></Suspense>)}
        {page === "portal-history" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><HistoryScreen /></Suspense>)}
        {page === "portal-showcase" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><ShowcaseScreen /></Suspense>)}
        {page === "portal-bookings" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><BookingsScreen /></Suspense>)}
        {page === "portal-media" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><MediaLibraryScreen /></Suspense>)}
        {page === "portal-editorial" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><EditorialScreen /></Suspense>)}
        {page === "portal-aura" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><AuraScreen /></Suspense>)}
        {page === "portal-events" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><EventsScreen /></Suspense>)}
        {page === "portal-magazine" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><MagazineScreen /></Suspense>)}
        {page === "portal-quarterly" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><QuarterlyScreen /></Suspense>)}
        {page === "portal-market" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><MarketScreen /></Suspense>)}
        {page === "portal-work" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><WorkQueueScreen /></Suspense>)}
        {page === "portal-achievements" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><AchievementsScreen /></Suspense>)}
        {page === "portal-intelligence" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><IntelligenceCentreScreen /></Suspense>)}
        {page === "portal-moments" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><SuccessMomentsScreen /></Suspense>)}
        {page === "portal-wins" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><WinsScreen /></Suspense>)}
        {page === "portal-recognition" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><RecognitionScreen /></Suspense>)}
        {page === "portal-next" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><ComingNextScreen /></Suspense>)}
        {page === "portal-strategic" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><StrategicIntelligenceScreen /></Suspense>)}
        {page === "portal-strategy" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><YourStrategyScreen /></Suspense>)}
        {page === "portal-impact" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><PartnershipImpactScreen /></Suspense>)}
        {page === "portal-recommend" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><RecommendedNextStepsScreen /></Suspense>)}
        {page === "portal-projection" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><ForwardProjectionScreen /></Suspense>)}
        {page === "portal-estate" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><DigitalEstateScreen /></Suspense>)}
        {page === "portal-review" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><PartnershipReviewScreen /></Suspense>)}
        {page === "portal-couple" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><CoupleScreen /></Suspense>)}
        {page === "portal-collections" && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><CollectionsScreen /></Suspense>)}
        {typeof page === "string" && page.startsWith("portal-ph-") && (<Suspense fallback={<div style={{ minHeight: "100vh", background: "#F1EFE9" }} />}><SectionPlaceholder /></Suspense>)}
        {page === "business-knowledge" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#f4f0e8" }} />}>
            <BusinessKnowledge />
          </Suspense>
        )}
        {page === "teach-aura" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#f4f0e8" }} />}>
            <TeachAura />
          </Suspense>
        )}
        {page === "knowledge-review" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#f4f0e8" }} />}>
            <KnowledgeReview />
          </Suspense>
        )}
        {page === "build-presence" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#f4f0e8" }} />}>
            <BuildPresence />
          </Suspense>
        )}
        {/* /real-weddings, page hidden until rebuilt to the editorial
            standard. Direct URL hits redirect to home rather than
            rendering the legacy SaaS layout. Restore both mounts when
            the rebuild lands. */}
        {(page === "real-weddings" || page === "real-wedding-detail") && (
          <HiddenRouteRedirect onRedirect={goHome} />
        )}
        {page === "shortlist" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ShortlistPage onBack={goHome} />
          </Suspense>
        )}
        {page === "vendor-directory" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <RegionCategoryPage
              onBack={goHome}
              onBackHome={goHome}
              onViewVenue={goVenue}
              onViewVendor={goVendorPublic}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              onViewCountry={goCountry}
              onViewPlanner={(planner) => goVenue(planner)}
              countrySlug={null}
              regionSlug={null}
              categorySlug="wedding-vendors"
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "couple-signup" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <CoupleSignup onSignupSuccess={goCoupleLogin} />
          </Suspense>
        )}
        {page === "couple-login" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <CoupleLogin onLoginSuccess={goGettingMarried} />
          </Suspense>
        )}
        {page === "couple-confirm-email" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <CoupleConfirmEmail />
          </Suspense>
        )}
        {page === "couple-forgot-password" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <CoupleForgotPassword />
          </Suspense>
        )}
        {page === "couple-reset-password" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0906" }} />}>
            <CoupleResetPassword />
          </Suspense>
        )}
        {page === "getting-married" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0B0906", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-heading-primary,'Gilda Display',serif)", fontSize: 28, letterSpacing: "0.15em", color: "#d39a21" }}>Loading LWD</div>}>
            <ProtectedCoupleRoute>
              <GettingMarriedDashboard onBack={goHome} footerNav={footerNav} />
            </ProtectedCoupleRoute>
          </Suspense>
        )}
        {page === "join" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <JoinPage />
          </Suspense>
        )}
        {page === "membership-edition" && (
          <MembershipEditionPage token={activeEditionToken} />
        )}

        {page === "fashion-studio" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0D0D0D" }} />}>
            <FashionStudioLandingPage onNavigateHome={goHome} />
          </Suspense>
        )}
        {page === "fashion-studio-showroom" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0D0D0D" }} />}>
            <FashionStudioLandingPage onNavigateHome={goHome} initialShowroomSlug={activeBoutiqueSlug} />
          </Suspense>
        )}
        {page === "fashion-studio-app" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#141414" }} />}>
            <FashionStudioPage onNavigateHome={goHome} />
          </Suspense>
        )}
        {page === "apply-for-inclusion" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ApplyForInclusionPage
              onToggleDark={toggleDark}
              darkMode={darkMode}
              onVendorLogin={goVendorLogin}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {["venue-pricing-application","vendor-pricing-application","boutique-pricing-application"].includes(page) && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0b0a08" }} />}>
            <PricingApplicationPage segment={page.replace("-pricing-application","")} onBack={goHome} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "advertise" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <AdvertisePage
              onNavigateHome={goHome}
              onNavigateApplyForInclusion={goApplyForInclusion}
            />
          </Suspense>
        )}
        {page === "pricing" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <PricingPage
              onNavigateHome={goHome}
              onNavigateApplyForInclusion={goApplyForInclusion}
              footerNav={footerNav}
              onToggleDark={toggleDark}
              darkMode={darkMode}
              onVendorLogin={goVendorLogin}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
            />
          </Suspense>
        )}
        {page === "listings" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <ListingsPage
              onBack={goHome}
              onViewVenue={goVenue}
              onNavigateHome={goHome}
              onToggleDark={toggleDark}
              darkMode={darkMode}
              onVendorLogin={goVendorLogin}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "all-wedding-venues" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0d0b09" }} />}>
            <AllWeddingVenuesPage
              onViewVenue={goVenue}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "h2" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <H2Page
              onBack={goHome}
              onViewVenue={goVenue}
              onNavigateHome={goHome}
              onToggleDark={toggleDark}
              darkMode={darkMode}
              onVendorLogin={goVendorLogin}
              onNavigateStandard={goStandard}
              onNavigateAbout={goAbout}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {page === "h3" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <H3
              onViewVenue={goVenue}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              goCountry={goCountry}
              goRegion={goRegion}
              onViewStandard={goStandard}
              onViewAbout={goAbout}
              onViewContact={goContact}
              onViewPartnership={goPartnership}
              onViewVendor={goVendor}
              onViewAdmin={goAdmin}
              onViewUSA={goUSA}
              onViewItaly={goItaly}
              onViewCountry={goCountry}
              onViewMagazine={goMagazine}
              onViewMagazineArticle={goMagazineArticle}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {/* ── /search?q=, universal search results ── */}
        {page === "search" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0B0906" }} />}>
            <SearchResultsPage
              query={activeSearchQuery}
              onBack={goHome}
              onViewVenue={goVenue}
              onViewCategory={goCategory}
              onViewRegion={goRegion}
              onViewRegionCategory={goRegionCategory}
              onViewCountry={goCountry}
              onViewMagazineArticle={goEditorialArticle}
              onViewAtlas={goRegionGuide}
              onViewPublication={goPublicationsReader}
              onViewRealWedding={goRealWeddingDetail}
              onSearch={goSearch}
              footerNav={footerNav}
            />
          </Suspense>
        )}
        {/* ── Root "/", renders whichever homepage variant is active ── */}
        {page === "root" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0B0906" }} />}>
            {activeHomepage === "h2"
              ? <H2Page onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
              : activeHomepage === "h4"
              ? <H4 onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
              : activeHomepage === "home"
              ? <HomePage onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} goCountry={goCountry} goRegion={goRegion} goAllDestinations={goAllDestinations} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
              : <H7 onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} goCountry={goCountry} goRegion={goRegion} goAllDestinations={goAllDestinations} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
            }
          </Suspense>
        )}
        {page === "h7" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <H7 onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} goCountry={goCountry} goRegion={goRegion} goAllDestinations={goAllDestinations} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "h4" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <H4 onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "partner-enquiry" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <PartnerEnquiryPage footerNav={footerNav} onBack={goHome} onNavigateStandard={goStandard} onNavigateAbout={goAbout} />
          </Suspense>
        )}
        {page === "most-loved" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <MostLovedPage onViewVenue={goVenue} />
          </Suspense>
        )}
        {page === "home" && (
          <HomePage onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} goCountry={goCountry} goRegion={goRegion} goAllDestinations={goAllDestinations} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
        )}
        {page === "h6" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <H6 onViewVenue={goVenue} onViewCategory={goCategory} onViewRegion={goRegion} onViewRegionCategory={goRegionCategory} goCountry={goCountry} goRegion={goRegion} goAllDestinations={goAllDestinations} onViewStandard={goStandard} onViewAbout={goAbout} onViewContact={goContact} onViewPartnership={goPartnership} onViewVendor={goVendor} onViewAdmin={goAdmin} onViewUSA={goUSA} onViewItaly={goItaly} onViewCountry={goCountry} onViewMagazine={goEditorial} onViewMagazineArticle={goEditorialArticle} onViewAtlas={goRegionGuide} onViewPublication={goPublicationsReader} onViewRealWedding={goRealWeddingDetail} onViewSearch={goSearch} footerNav={footerNav} />
          </Suspense>
        )}
        {page === "not-found" && (
          <Suspense fallback={<div style={{ minHeight: "100vh", background: "#FAF8F4" }} />}>
            <NotFoundPage onNavigateHome={goHome} onNavigateCategory={goCategory} />
          </Suspense>
        )}

        {/* ── Global site footer ──
            Rendered here for ALL pages except auth/dashboard pages listed below.
            RULE: Never import or render <SiteFooter> inside a page component that
            is served through this main.jsx render tree, it will double-render. ── */}
        {(() => {
          const FOOTER_EXCLUDED = ["membership-edition","admin","admin-login","admin-oauth-callback","vendor","vendor-login","vendor-signup","vendor-activate","vendor-confirm-email","vendor-forgot-password","vendor-reset-password","portal","getting-married","magazine-studio","couple-signup","couple-login","couple-confirm-email","couple-forgot-password","couple-reset-password","event-review","publications-reader","publications-embed","publication-studio","social-canvas","studio-thumbnail","magazine-reader","magazine-collaborate","magazine-reach","magazine-proof-approval","preview","fashion-studio-app"];
          if (FOOTER_EXCLUDED.includes(page)) return null;
          // The LWD Edit article opened as a sealed proof link (?preview=1) gets no
          // SiteFooter, so a prospect has no footer links out into the directory.
          if (page === 'editorial-article' && isSealedPreview(page)) return null;
          // Inside a private-review preview session, the genuinely sealed content
          // pages (the listing profile and its reviews) get the minimal brand
          // footer instead of the full SiteFooter, so prospects never reach
          // Browse/Shop/Membership pricing from the footer. The standalone
          // /showcase/:slug is a PUBLIC marketing page (own masthead, indexable),
          // so it always gets the full SiteFooter, even if a stale preview_session
          // flag lingers in this browser from testing a review.
          let inPreview = false;
          try { inPreview = !!sessionStorage.getItem('preview_session'); } catch { /* partitioned storage */ }
          if (inPreview && ["listing-profile","venue-reviews"].includes(page)) {
            return <PreviewMiniFooter />;
          }
          return <SiteFooter onNavigateAdmin={goAdmin} />;
        })()}

        {/* ── Aura suppressor, keeps chat closed on all dashboard / auth pages ── */}
        <AuraSuppressor active={["admin","admin-login","admin-oauth-callback","vendor","vendor-login","vendor-signup","vendor-activate","vendor-confirm-email","vendor-forgot-password","vendor-reset-password","portal","couple-signup","couple-login","couple-confirm-email","couple-forgot-password","couple-reset-password","join","studio-thumbnail","membership-edition","publications","publications-reader","publications-embed","publication-studio","social-canvas","magazine-reader","magazine-collaborate","magazine-reach","magazine-proof-approval","preview"].includes(page) || isSealedPreview(page)} />

        {/* ── Global chat system, hidden on dashboards, auth, publication, preview,
            and sealed preview-session pages (keeps the review distraction-free) ── */}
        {!(["admin","admin-login","admin-oauth-callback","vendor","vendor-login","vendor-signup","vendor-activate","vendor-confirm-email","vendor-forgot-password","vendor-reset-password","portal","couple-signup","couple-login","couple-confirm-email","couple-forgot-password","couple-reset-password","join","studio-thumbnail","membership-edition","publications","publications-reader","publications-embed","publication-studio","social-canvas","magazine-reader","magazine-collaborate","magazine-reach","magazine-proof-approval","preview"].includes(page) || isSealedPreview(page)) && (
          <Suspense fallback={null}>
            <AuraChat onNavigateHome={goHome} />
          </Suspense>
        )}

        {/* ── Global admin edit bar, visible on live pages for authenticated admins ── */}
        <GlobalAdminBar
          page={page}
          slugs={{
            showcaseSlug:  activeShowcaseSlug,
            venueSlug:     activeVenueSlug,
            magazineSlug:  activeMagazineSlug,
            eventSlug:     activeEventSlug,
            locationSlug:  activeLocationSlug,
            countrySlug:   activeCountrySlug,
            regionSlug:    activeRegionSlug,
          }}
          onOpenAdmin={goAdmin}
        />

        {/* ── Global compare strip + modal, persists across all pages ── */}
        <Suspense fallback={null}>
          <GlobalCompare />
        </Suspense>

        {/* ── GDPR cookie banner, hidden on the thumbnail renderer and on a private
             Membership Edition, where it covers the foot of the magazine and asks a
             named member for consent on a link we sent them personally ── */}
        {page !== "studio-thumbnail" && page !== "membership-edition" && <CookieBanner />}

        {/* ── Site-wide SEO head tags, RSS discovery + verification codes ── */}
        <GlobalSeoHead />
        {(() => {
          // Manifest-driven runtime identity for authority statics that carry no per-component
          // <SearchIdentity>. Path-keyed off the live URL so it fires regardless of the router
          // `page` value (Page Studio pages share page==="ps-page"). Prerender captures this into
          // the flat HTML; the raw-HTML acceptance test is against /careers.
          const rawPath = typeof window !== "undefined" ? window.location.pathname : "/";
          return needsRuntimeIdentity(rawPath) ? <SearchIdentity path={rawPath} /> : null;
        })()}

        {/* ── Tracking & Analytics, admin-managed scripts, fires on every page ── */}
        <TrackingScripts />

        {/* WhatsApp button removed, re-enable by importing WhatsAppButton and restoring this block */}

        {/* ── Favicon, admin-managed via Site Identity settings ── */}
        <SiteFavicon />

        {/* ── Noindex private/admin/studio routes ───────────────────────── */}
        {[
          'admin', 'admin-login', 'admin-oauth-callback',
          'publication-studio', 'social-canvas', 'magazine-studio', 'studio-edit', 'studio-thumbnail',
          'vendor', 'portal', 'vendor-login', 'vendor-signup', 'vendor-activate',
          'vendor-confirm-email', 'vendor-forgot-password', 'vendor-reset-password',
          'couple-login', 'couple-signup', 'couple-confirm-email',
          'couple-forgot-password', 'couple-reset-password',
          'luxury-region-test', 'grid-responsive-test', 'lwd-dev',
        ].includes(page) && (
          <Helmet>
            <meta name="robots" content="noindex,nofollow" />
          </Helmet>
        )}

            </FloatingUiManager>
            </AuraProvider>
          </ShortlistProvider>
        </CoupleAuthProvider>
      </VendorAuthProvider>
    </AdminAuthProvider>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
createRoot(document.getElementById("root")).render(
  <StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </HelmetProvider>
  </StrictMode>
);
